import { isGalleryContentType } from "../../shared/gallery-content";
import { apiFetch as fetch } from "./api-fetch";
import type { BitmapDistrict, BitmapGalleryPage, BitmapMarketPage, BitmapOwnedPage } from "./bitmap";
import type { BitmapInscriptionExport, BitmapRendererExport } from "./bitmap-inscription";
import { hex } from "@scure/base";
import { Transaction } from "@scure/btc-signer";
import type {
  AppConfig,
  InscriptionOutputManifestItem,
  InscriptionRareSatManifestItem,
  MarketplaceFeeChallenge,
  MarketplaceFeeProof,
  MarketplaceFeeQuoteResult,
  PartnerInscriberFeeQuote,
  PartnerAuthChallenge,
  PartnerAuthSession,
  MempoolUtxo,
  OrdinalInscriptionRecord,
  OrdinalOutputRecord,
  OrdinalSource,
  RecommendedFees,
  SpecialSatProfile,
  SpecificSatResolution,
  WalletSatSource,
  WalletProviderId,
} from "./types";
import type {
  AuctionBidQuote,
  AuctionDetail,
  AuctionInput,
  AuctionListView,
  AuctionPage,
  AuctionPreparation,
  AuctionRecord,
} from "./auctions";
import type { HiveUploadAuthorization, HiveUploadReceipt } from "./hive";
import type { RuneEtchAvailability, RuneEtchDefinition, RuneEtchJobSummary } from "./runes";
import type {
  HiveExchangeAccount,
  HiveExchangeAsset,
  HiveExchangeChart,
  HiveExchangeChartRange,
  HiveExchangeConfig,
  HiveExchangeDepositAddress,
  HiveExchangeFreeplayAssetGrantInput,
  HiveExchangeMarket,
  HiveExchangeOrder,
  HiveExchangeOrderbook,
  HiveExchangeReferencePrice,
  HiveExchangeSide,
  HiveExchangeWithdrawal,
} from "./hive-exchange";
import type { WalletAccount } from "./account";
import type { AdminChallenge, AdminSession, AdminStatsPage, AdminWalletPage } from "./admin";
import type {
  FeatureTweetAdminPage,
  FeatureTweetHealth,
  FeatureTweetInput,
  FeatureTweetRescheduleInput,
  FeatureTweetRecord,
  FeatureTweetSettingsInput,
} from "./feature-tweets";
import { verifiedInscriptionTransactionStatus, type InscriptionHistoryPage, type InscriptionTransactionStatuses } from "./history";
import type { InscriptionBurnQuote, InscriptionBurnResult } from "./burn";
import type {
  CreatorProfile,
  CreatorProfileInput,
  CreatorProfilePage,
  CreatorInscriptionPage,
  CreatorOwnedInscriptionPage,
  LaunchDraftInput,
  LaunchAllowlistAdditionsInput,
  LaunchAdminEditInput,
  LaunchEditionQuote,
  LaunchMediaKind,
  LaunchMediaUpload,
  LaunchOnDemandAsset,
  LaunchOnDemandQuote,
  LaunchOnDemandEstimate,
  LaunchpadChallenge,
  LaunchpadEligibility,
  LaunchpadMintQuote,
  LaunchpadShieldedMintQuote,
  LaunchpadShieldedPreparationQuote,
  LaunchpadMintRecord,
  LaunchpadMintResult,
  LaunchpadPlatformStatus,
  LaunchpadAdminIdentity,
  LaunchpadAdminQueue,
  LaunchpadAdminQueueStatus,
  DeferredInscriptionBatchQuote,
  DeferredInscriptionOrder,
  LaunchpadPreparationBatch,
  LaunchpadSession,
  LaunchRecord,
  LaunchOnDemandGalleryPage,
  LaunchPresentationInput,
  LaunchSigningMessage,
  LaunchStageTimingUpdateInput,
} from "./launchpad";
import type {
  MarketplaceActivityPage,
  MarketplaceCollectionDetailPage,
  MarketplaceCollectionManager,
  MarketplaceCollectionTheme,
  MarketplaceCollectionPage,
  MarketplaceCollectionSubmission,
  MarketplaceCollectionSubmissionPage,
  MarketplaceCollectionSubmissionType,
  MarketplaceConfig,
  MarketplaceInscription,
  MarketplaceListing,
  MarketplaceListingPreparation,
  MarketplacePurchaseQuote,
  MarketplacePurchaseResult,
  MarketplacePublicWalletPage,
  MarketplaceRareInventoryPage,
  MarketplaceRareListing,
  MarketplaceRareListingPage,
  MarketplaceRareListingPreparation,
  MarketplaceRarePurchaseQuote,
  MarketplaceRarePurchaseResult,
  MarketplaceRareSalePage,
  MarketplaceRareWalletPage,
  MarketplaceRareWalletTab,
  MarketplaceSalePage,
  MarketplaceWalletPage,
  MarketplaceWalletTab,
} from "./marketplace";
import type {
  MarketplaceSwapAcceptanceQuote,
  MarketplaceSwapActivityPage,
  MarketplaceSwapCompletion,
  MarketplaceSwapConfig,
  MarketplaceSwapCreateInput,
  MarketplaceSwapFilters,
  MarketplaceSwapMakerAction,
  MarketplaceSwapMyTab,
  MarketplaceSwapOffer,
  MarketplaceSwapOfferPage,
} from "./marketplace-swap";
import type { ShieldedHauntlingsGalleryFilter, ShieldedHauntlingsGalleryPage, ShieldedV2PublicPage } from "./shielded-v2";
import type {
  FinanceCollectionPage,
  FinanceOrder,
  FinanceOrderInput,
  FinanceOrderPage,
  FinancePortfolio,
  FinanceSigningAction,
  FinanceSpendQuote,
  FinanceTransactionResult,
} from "./finance";
import type {
  ToolsInventory,
  ToolsQuote,
  ToolsSplitOutputRequest,
  ToolsToolDefinition,
  ToolsToolId,
  ToolsTransactionResult,
} from "./tools";
import type {
  RuneClaimPrepareRequest,
  RuneClaimQuote,
  RuneClaimsConfig,
  RuneClaimSubmitResult,
  RuneClaimSummary,
} from "./rune-claims";
import { walletSatFromOutput, walletSatSourceIsUsable } from "./rarity";

export class ApiRequestError extends Error {
  constructor(message: string, readonly revealNotSent = false, readonly broadcastStatus?: "rejected" | "unknown", readonly retryableValidation = false, readonly reconcilableSubmission = false) {
    super(message); this.name = "ApiRequestError";
  }
}

async function expectResponse(response: Response): Promise<Response> {
  if (response.ok) return response;
  const message = await response.text().catch(() => "");
  if (message) {
    type ErrorBody = { error?: unknown; revealNotSent?: unknown; status?: unknown; code?: unknown };
    let parsed: ErrorBody | null = null;
    try {
      parsed = JSON.parse(message) as ErrorBody;
    } catch {
      // Upstream Bitcoin APIs also return useful plain-text errors.
    }
    if (typeof parsed?.error === "string") throw new ApiRequestError(
      parsed.error,
      parsed.status !== "unknown" && parsed.revealNotSent === true,
      parsed.status === "rejected" || parsed.status === "unknown" ? parsed.status : undefined,
      response.status === 503 && parsed.code === "BITCOIN_INDEX_UNAVAILABLE" && parsed.revealNotSent === true && parsed.status === undefined,
    );
  }
  throw new Error(message || `Request failed with status ${response.status}`);
}

export async function fetchAppConfig(): Promise<AppConfig> {
  const response = await expectResponse(await fetch("/api/config", { headers: { accept: "application/json" } }));
  return response.json() as Promise<AppConfig>;
}

export async function fetchRecommendedFees(): Promise<RecommendedFees> {
  const response = await expectResponse(await fetch("/api/fees", { headers: { accept: "application/json" } }));
  return response.json() as Promise<RecommendedFees>;
}

export async function requestHiveUploadAuthorizations(authorization: string, files: File[]): Promise<HiveUploadAuthorization[]> {
  const response = await expectResponse(await fetch("/api/hive/uploads", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ files: files.map((file) => ({ name: file.name, mimeType: file.type || "application/octet-stream", size: file.size })) }),
  }));
  const result = await response.json() as { uploads: HiveUploadAuthorization[] };
  return result.uploads;
}

export async function completeHiveUpload(authorization: string, uploadId: string, receipt: string): Promise<HiveUploadReceipt> {
  const response = await expectResponse(await fetch(`/api/hive/uploads/${encodeURIComponent(uploadId)}/complete`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ receipt }),
  }));
  const result = await response.json() as { asset: HiveUploadReceipt };
  return result.asset;
}

export interface FinanceConfig {
  network: "mainnet";
  platformPublicKey: string;
  minimumPrincipalSats: number;
  maximumPrincipalSats: number;
  activationFeeSats: number;
  interestFeeBps: number;
  minimumInterestFeeSats: number;
  serviceFeeAddress: string;
}

export async function fetchFinanceConfig(): Promise<FinanceConfig> {
  const response = await expectResponse(await fetch("/api/finance/config", { headers: { accept: "application/json" } }));
  return response.json() as Promise<FinanceConfig>;
}

export async function fetchAuctions(page = 0, authorization?: string, view: AuctionListView = "open", filters: { format?: string; query?: string } = {}): Promise<AuctionPage> {
  const params = new URLSearchParams({ page: String(page), status: view });
  if (filters.format) params.set("format", filters.format);
  if (filters.query) params.set("query", filters.query);
  const response = await expectResponse(await fetch(`/api/auctions?${params.toString()}`, { headers: launchpadHeaders(authorization) }));
  return response.json() as Promise<AuctionPage>;
}

export async function fetchMyAuctions(authorization: string, page = 0, filters: { format?: string; query?: string } = {}): Promise<AuctionPage> {
  const params = new URLSearchParams({ page: String(page), mine: "true" });
  if (filters.format) params.set("format", filters.format);
  if (filters.query) params.set("query", filters.query);
  const response = await expectResponse(await fetch(`/api/auctions?${params}`, { headers: launchpadHeaders(authorization) }));
  return response.json() as Promise<AuctionPage>;
}

export async function fetchAuction(auctionId: string, authorization?: string): Promise<AuctionDetail> {
  const response = await expectResponse(await fetch(`/api/auctions/${encodeURIComponent(auctionId)}`, { headers: launchpadHeaders(authorization) }));
  const result = await response.json() as { auction: AuctionDetail };
  return result.auction;
}

export async function createAuction(authorization: string, input: AuctionInput): Promise<AuctionPreparation> {
  const response = await expectResponse(await fetch("/api/auctions", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(input),
  }));
  const result = await response.json() as { preparation: AuctionPreparation };
  return result.preparation;
}

export async function activateAuction(authorization: string, auctionId: string, signedPsbtBase64: string): Promise<AuctionRecord> {
  const response = await expectResponse(await fetch(`/api/auctions/${encodeURIComponent(auctionId)}/submit`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ signedPsbtBase64 }),
  }));
  const result = await response.json() as { auction: AuctionRecord };
  return result.auction;
}

export async function quoteAuctionBid(
  authorization: string,
  auctionId: string,
  bidSats: number,
  feeRate: number,
  utxos: MempoolUtxo[],
): Promise<AuctionBidQuote> {
  const response = await expectResponse(await fetch(`/api/auctions/${encodeURIComponent(auctionId)}/bid/quote`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ bidSats, feeRate, utxos }),
  }));
  const result = await response.json() as { quote: AuctionBidQuote };
  return result.quote;
}

export async function recordAuctionBid(
  authorization: string,
  auctionId: string,
  reservationId: string,
  signedPsbtBase64: string,
): Promise<{ auction: AuctionDetail; bid: import("./auctions").AuctionBid }> {
  const response = await expectResponse(await fetch(`/api/auctions/${encodeURIComponent(auctionId)}/bid/record`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ reservationId, signedPsbtBase64 }),
  }));
  return response.json() as Promise<{ auction: AuctionDetail; bid: import("./auctions").AuctionBid }>;
}

export async function closeAuction(authorization: string, auctionId: string): Promise<AuctionDetail> {
  const response = await expectResponse(await fetch(`/api/auctions/${encodeURIComponent(auctionId)}/close`, {
    method: "POST",
    headers: launchpadHeaders(authorization),
  }));
  const result = await response.json() as { auction: AuctionDetail };
  return result.auction;
}

export async function cancelAuction(authorization: string, auctionId: string): Promise<AuctionDetail> {
  const response = await expectResponse(await fetch(`/api/auctions/${encodeURIComponent(auctionId)}/cancel`, {
    method: "POST",
    headers: launchpadHeaders(authorization),
  }));
  const result = await response.json() as { auction: AuctionDetail };
  return result.auction;
}

export async function fetchFinanceCollections(query = "", page = 0): Promise<FinanceCollectionPage> {
  const params = new URLSearchParams({ query, page: String(page) });
  const response = await expectResponse(await fetch(`/api/finance/collections?${params.toString()}`, { headers: { accept: "application/json" } }));
  return response.json() as Promise<FinanceCollectionPage>;
}

export async function fetchFinanceMarketCollections(
  query = "",
  page = 0,
  side: "all" | "request" | "offer" = "all",
): Promise<FinanceCollectionPage> {
  const params = new URLSearchParams({ query, page: String(page), side });
  const response = await expectResponse(await fetch(`/api/finance/markets?${params.toString()}`, { headers: { accept: "application/json" } }));
  return response.json() as Promise<FinanceCollectionPage>;
}

export async function fetchFinanceOrders(side: "all" | "request" | "offer" = "all", page = 0, collectionSlug = ""): Promise<FinanceOrderPage> {
  const params = new URLSearchParams({ side, page: String(page) });
  if (collectionSlug) params.set("collection", collectionSlug);
  const response = await expectResponse(await fetch(`/api/finance/orders?${params.toString()}`, { headers: { accept: "application/json" } }));
  return response.json() as Promise<FinanceOrderPage>;
}

export async function createFinanceOrder(
  authorization: string,
  input: FinanceOrderInput,
  signature: string,
  lenderUtxos?: MempoolUtxo[],
): Promise<FinanceOrder> {
  const response = await expectResponse(await fetch("/api/finance/orders", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ ...input, signature, lenderUtxos }),
  }));
  const result = await response.json() as { order: FinanceOrder };
  return result.order;
}

export async function cancelFinanceOrder(authorization: string, orderId: string): Promise<void> {
  await expectResponse(await fetch(`/api/finance/orders/${encodeURIComponent(orderId)}/cancel`, {
    method: "POST",
    headers: launchpadHeaders(authorization),
  }));
}

export async function cancelFinanceMatch(authorization: string, matchId: string): Promise<void> {
  await expectResponse(await fetch(`/api/finance/matches/${encodeURIComponent(matchId)}/cancel`, {
    method: "POST",
    headers: launchpadHeaders(authorization),
  }));
}

export async function acceptFinanceOrder(
  authorization: string,
  orderId: string,
  feeRate: number,
  lenderUtxos?: MempoolUtxo[],
  inscriptionId?: string,
): Promise<FinanceSigningAction> {
  const response = await expectResponse(await fetch(`/api/finance/orders/${encodeURIComponent(orderId)}/accept`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ feeRate, lenderUtxos, inscriptionId }),
  }));
  const result = await response.json() as { action: FinanceSigningAction };
  return result.action;
}

export async function fetchFinancePortfolio(authorization: string, page = 0): Promise<FinancePortfolio> {
  const response = await expectResponse(await fetch(`/api/finance/portfolio?page=${page}`, { headers: launchpadHeaders(authorization) }));
  return response.json() as Promise<FinancePortfolio>;
}

function hiveExchangePrefix(mode: import("./hive-exchange").HiveExchangeMode): string {
  return mode === "freeplay" ? "/api/finance/hive/freeplay" : "/api/finance/hive";
}

export async function fetchHiveExchangeConfig(mode: import("./hive-exchange").HiveExchangeMode = "live"): Promise<HiveExchangeConfig> {
  const response = await expectResponse(await fetch(`${hiveExchangePrefix(mode)}/config`, { headers: { accept: "application/json" } }));
  return response.json() as Promise<HiveExchangeConfig>;
}

export async function fetchHiveExchangeMarkets(mode: import("./hive-exchange").HiveExchangeMode = "live"): Promise<HiveExchangeMarket[]> {
  const response = await expectResponse(await fetch(`${hiveExchangePrefix(mode)}/markets`, { headers: { accept: "application/json" } }));
  const result = await response.json() as { markets: HiveExchangeMarket[] };
  return result.markets;
}

export async function fetchHiveExchangeOrderbook(
  marketId: string,
  mode: import("./hive-exchange").HiveExchangeMode = "live",
): Promise<HiveExchangeOrderbook> {
  const response = await expectResponse(await fetch(
    `${hiveExchangePrefix(mode)}/markets/${encodeURIComponent(marketId)}/orderbook`,
    { headers: { accept: "application/json" } },
  ));
  return response.json() as Promise<HiveExchangeOrderbook>;
}

export async function fetchHiveExchangeChart(
  marketId: string,
  range: HiveExchangeChartRange,
  mode: import("./hive-exchange").HiveExchangeMode = "live",
): Promise<HiveExchangeChart> {
  const response = await expectResponse(await fetch(
    `${hiveExchangePrefix(mode)}/markets/${encodeURIComponent(marketId)}/chart?range=${encodeURIComponent(range)}`,
    { headers: { accept: "application/json" } },
  ));
  return response.json() as Promise<HiveExchangeChart>;
}

export async function fetchHiveExchangeReferencePrice(
  marketId: string,
  mode: import("./hive-exchange").HiveExchangeMode = "live",
): Promise<HiveExchangeReferencePrice> {
  const response = await expectResponse(await fetch(
    `${hiveExchangePrefix(mode)}/markets/${encodeURIComponent(marketId)}/reference-price`,
    { headers: { accept: "application/json" } },
  ));
  return response.json() as Promise<HiveExchangeReferencePrice>;
}

export async function fetchHiveExchangeAccount(
  authorization: string,
  marketId: string,
  mode: import("./hive-exchange").HiveExchangeMode = "live",
): Promise<HiveExchangeAccount> {
  const response = await expectResponse(await fetch(
    `${hiveExchangePrefix(mode)}/markets/${encodeURIComponent(marketId)}/account`,
    { headers: launchpadHeaders(authorization) },
  ));
  return response.json() as Promise<HiveExchangeAccount>;
}

export async function requestHiveExchangeDepositAddress(
  authorization: string,
  marketId: string,
  asset: HiveExchangeAsset,
): Promise<HiveExchangeDepositAddress> {
  const response = await expectResponse(await fetch(`/api/finance/hive/markets/${encodeURIComponent(marketId)}/deposit-address`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ asset }),
  }));
  const result = await response.json() as { depositAddress: HiveExchangeDepositAddress };
  return result.depositAddress;
}

export async function placeHiveExchangeOrder(
  authorization: string,
  marketId: string,
  input: {
    clientOrderId: string;
    side: HiveExchangeSide;
    priceTicks: number;
    quantityLots: number;
    timeInForce?: import("./hive-exchange").HiveExchangeTimeInForce;
  },
  mode: import("./hive-exchange").HiveExchangeMode = "live",
): Promise<HiveExchangeOrder> {
  const response = await expectResponse(await fetch(`${hiveExchangePrefix(mode)}/markets/${encodeURIComponent(marketId)}/orders`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(input),
  }));
  const result = await response.json() as { order: HiveExchangeOrder };
  return result.order;
}

export async function cancelHiveExchangeOrder(
  authorization: string,
  marketId: string,
  orderId: string,
  mode: import("./hive-exchange").HiveExchangeMode = "live",
): Promise<HiveExchangeOrder> {
  const response = await expectResponse(await fetch(
    `${hiveExchangePrefix(mode)}/markets/${encodeURIComponent(marketId)}/orders/${encodeURIComponent(orderId)}/cancel`,
    { method: "POST", headers: launchpadHeaders(authorization) },
  ));
  const result = await response.json() as { order: HiveExchangeOrder };
  return result.order;
}

export async function resetHiveFreeplayAccount(authorization: string, marketId: string): Promise<HiveExchangeAccount> {
  const response = await expectResponse(await fetch(
    `${hiveExchangePrefix("freeplay")}/markets/${encodeURIComponent(marketId)}/reset`,
    { method: "POST", headers: launchpadHeaders(authorization) },
  ));
  return response.json() as Promise<HiveExchangeAccount>;
}

export async function addHiveFreeplayAsset(
  authorization: string,
  marketId: string,
  input: HiveExchangeFreeplayAssetGrantInput,
): Promise<HiveExchangeAccount> {
  const response = await expectResponse(await fetch(
    `${hiveExchangePrefix("freeplay")}/markets/${encodeURIComponent(marketId)}/assets`,
    {
      method: "POST",
      headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
      body: JSON.stringify(input),
    },
  ));
  return response.json() as Promise<HiveExchangeAccount>;
}

export async function reseedHiveFreeplayMarket(
  authorization: string,
  marketId: string,
): Promise<{ seeded: boolean; stats: { simulatedAccounts: number; openOrders: number; historicalTrades: number; referencePriceTicks: number } }> {
  const response = await expectResponse(await fetch(
    `${hiveExchangePrefix("freeplay")}/markets/${encodeURIComponent(marketId)}/reseed`,
    { method: "POST", headers: launchpadHeaders(authorization) },
  ));
  return response.json() as Promise<{ seeded: boolean; stats: { simulatedAccounts: number; openOrders: number; historicalTrades: number; referencePriceTicks: number } }>;
}

export function hiveExchangeStreamUrl(marketId: string, mode: import("./hive-exchange").HiveExchangeMode = "live"): string {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}${hiveExchangePrefix(mode)}/markets/${encodeURIComponent(marketId)}/stream`;
}

export async function requestHiveExchangeWithdrawal(
  authorization: string,
  marketId: string,
  input: { clientRequestId: string; asset: HiveExchangeAsset; amount: string },
): Promise<HiveExchangeWithdrawal> {
  const response = await expectResponse(await fetch(`/api/finance/hive/markets/${encodeURIComponent(marketId)}/withdrawals`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(input),
  }));
  const result = await response.json() as { withdrawal: HiveExchangeWithdrawal };
  return result.withdrawal;
}

export async function fetchFinanceMatchAction(authorization: string, matchId: string): Promise<FinanceSigningAction> {
  const response = await expectResponse(await fetch(`/api/finance/matches/${encodeURIComponent(matchId)}/action`, { headers: launchpadHeaders(authorization) }));
  const result = await response.json() as { action: FinanceSigningAction };
  return result.action;
}

export async function submitFinanceMatchSignature(
  authorization: string,
  matchId: string,
  signedPsbtBase64: string,
): Promise<{ match: import("./finance").FinanceMatch; transactionId?: string }> {
  const response = await expectResponse(await fetch(`/api/finance/matches/${encodeURIComponent(matchId)}/sign`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ signedPsbtBase64 }),
  }));
  return response.json() as Promise<{ match: import("./finance").FinanceMatch; transactionId?: string }>;
}

export async function prepareFinanceSpend(
  authorization: string,
  matchId: string,
  kind: "repay" | "default",
  feeRate: number,
  utxos: MempoolUtxo[],
): Promise<FinanceSpendQuote> {
  const response = await expectResponse(await fetch(`/api/finance/matches/${encodeURIComponent(matchId)}/${kind}/prepare`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ feeRate, utxos }),
  }));
  const result = await response.json() as { quote: FinanceSpendQuote };
  return result.quote;
}

export async function submitFinanceSpend(
  authorization: string,
  matchId: string,
  kind: "repay" | "default",
  signedPsbtBase64: string,
): Promise<FinanceTransactionResult> {
  const response = await expectResponse(await fetch(`/api/finance/matches/${encodeURIComponent(matchId)}/${kind}/submit`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ signedPsbtBase64 }),
  }));
  return response.json() as Promise<FinanceTransactionResult>;
}

export async function fetchInscriptionHistory(address: string, cursor?: string): Promise<InscriptionHistoryPage> {
  const params = new URLSearchParams({ address });
  if (cursor) params.set("cursor", cursor);
  const response = await expectResponse(await fetch(`/api/history?${params.toString()}`, {
    headers: { accept: "application/json" },
  }));
  return response.json() as Promise<InscriptionHistoryPage>;
}

export async function fetchInscriptionTransactionStatuses(txids: string[]): Promise<InscriptionTransactionStatuses> {
  if (txids.length === 0) return {};
  const response = await expectResponse(await fetch("/api/history/statuses", {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ txids: txids.slice(0, 100) }),
  }));
  const result = (await response.json()) as { statuses?: Record<string, unknown> };
  if (!result || typeof result.statuses !== "object" || result.statuses === null || Array.isArray(result.statuses)) {
    throw new Error("Transaction statuses could not be verified");
  }
  return Object.fromEntries(txids.slice(0, 100).map(txid => [
    txid, verifiedInscriptionTransactionStatus(txid, result.statuses?.[txid]),
  ]));
}

export async function requestAdminChallenge(address: string): Promise<AdminChallenge> {
  const response = await expectResponse(await fetch("/api/admin/challenge", {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ address }),
  }));
  return response.json() as Promise<AdminChallenge>;
}

export async function requestAdminSession(challengeToken: string, signature: string): Promise<AdminSession> {
  const response = await expectResponse(await fetch("/api/admin/session", {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ challengeToken, signature }),
  }));
  return response.json() as Promise<AdminSession>;
}

function adminHeaders(authorization: string): HeadersInit {
  return { accept: "application/json", authorization: `Bearer ${authorization}` };
}

function launchpadHeaders(authorization?: string): HeadersInit {
  return {
    accept: "application/json",
    ...(authorization ? { authorization: `Bearer ${authorization}` } : {}),
  };
}

export async function requestLaunchpadChallenge(paymentAddress: string, ordinalsAddress: string, paymentPublicKey: string, ordinalsPublicKey: string, provider?: WalletProviderId): Promise<LaunchpadChallenge> {
  const response = await expectResponse(await fetch("/api/launchpad/challenge", {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ paymentAddress, ordinalsAddress, paymentPublicKey, ordinalsPublicKey, provider }),
  }));
  return response.json() as Promise<LaunchpadChallenge>;
}

export async function requestLaunchpadSession(challengeToken: string, signature: string, ordinalsSignature?: string): Promise<LaunchpadSession> {
  const response = await expectResponse(await fetch("/api/launchpad/session", {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ challengeToken, signature, ordinalsSignature }),
  }));
  return response.json() as Promise<LaunchpadSession>;
}

export async function fetchWalletAccount(authorization: string): Promise<WalletAccount> {
  const response = await expectResponse(await fetch("/api/account/wallets", { headers: launchpadHeaders(authorization) }));
  const result = await response.json() as { account: WalletAccount };
  return result.account;
}

export async function linkWalletAccount(authorization: string, linkedAuthorization: string): Promise<WalletAccount> {
  const response = await expectResponse(await fetch("/api/account/wallets/link", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ authorization: linkedAuthorization }),
  }));
  const result = await response.json() as { account: WalletAccount };
  return result.account;
}

export async function removeWalletAccount(authorization: string, paymentAddress: string): Promise<WalletAccount> {
  const response = await expectResponse(await fetch(`/api/account/wallets/${encodeURIComponent(paymentAddress)}`, {
    method: "DELETE",
    headers: launchpadHeaders(authorization),
  }));
  const result = await response.json() as { account: WalletAccount };
  return result.account;
}

export async function setMainWalletAccount(authorization: string, paymentAddress: string): Promise<WalletAccount> {
  const response = await expectResponse(await fetch("/api/account/wallets/main", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ paymentAddress }),
  }));
  const result = await response.json() as { account: WalletAccount };
  return result.account;
}

export async function fetchMarketplaceConfig(): Promise<MarketplaceConfig> {
  const response = await expectResponse(await fetch("/api/marketplace/config", { headers: launchpadHeaders() }));
  return response.json() as Promise<MarketplaceConfig>;
}

export async function fetchMarketplaceSwapConfig(): Promise<MarketplaceSwapConfig> {
  const response = await expectResponse(await fetch("/api/marketplace/swaps/config", { headers: launchpadHeaders() }));
  return response.json() as Promise<MarketplaceSwapConfig>;
}

export async function fetchMarketplaceSwaps(filters: MarketplaceSwapFilters = {}, authorization?: string): Promise<MarketplaceSwapOfferPage> {
  const params = new URLSearchParams({ page: String(filters.page ?? 0) });
  if (filters.query) params.set("query", filters.query);
  if (filters.collection) params.set("collection", filters.collection);
  if (filters.requestKind) params.set("requestKind", filters.requestKind);
  if (filters.btc) params.set("btc", filters.btc);
  if (filters.status) params.set("status", filters.status);
  if (filters.compatible) params.set("compatible", "true");
  const response = await expectResponse(await fetch(`/api/marketplace/swaps?${params.toString()}`, { headers: launchpadHeaders(authorization) }));
  return response.json() as Promise<MarketplaceSwapOfferPage>;
}

export async function fetchMyMarketplaceSwaps(authorization: string, tab: MarketplaceSwapMyTab, page = 0): Promise<MarketplaceSwapOfferPage> {
  const params = new URLSearchParams({ tab, page: String(page) });
  const response = await expectResponse(await fetch(`/api/marketplace/swaps/me?${params.toString()}`, { headers: launchpadHeaders(authorization) }));
  return response.json() as Promise<MarketplaceSwapOfferPage>;
}

export async function createMarketplaceSwap(authorization: string, input: MarketplaceSwapCreateInput): Promise<MarketplaceSwapOffer> {
  const response = await expectResponse(await fetch("/api/marketplace/swaps", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(input),
  }));
  const result = await response.json() as { offer: MarketplaceSwapOffer };
  return result.offer;
}

export async function fetchMarketplaceSwap(offerId: string, token = "", authorization?: string): Promise<MarketplaceSwapOffer> {
  const params = token ? `?token=${encodeURIComponent(token)}` : "";
  const response = await expectResponse(await fetch(`/api/marketplace/swaps/${encodeURIComponent(offerId)}${params}`, { headers: launchpadHeaders(authorization) }));
  const result = await response.json() as { offer: MarketplaceSwapOffer };
  return result.offer;
}

export async function fetchMarketplaceSwapActivity(offerId: string, page = 0, token = "", authorization?: string): Promise<MarketplaceSwapActivityPage> {
  const params = new URLSearchParams({ page: String(page) });
  if (token) params.set("token", token);
  const response = await expectResponse(await fetch(`/api/marketplace/swaps/${encodeURIComponent(offerId)}/activity?${params}`, { headers: launchpadHeaders(authorization) }));
  return response.json() as Promise<MarketplaceSwapActivityPage>;
}

export async function refreshMarketplaceSwapShareLink(authorization: string, offerId: string): Promise<string> {
  const response = await expectResponse(await fetch(`/api/marketplace/swaps/${encodeURIComponent(offerId)}/share-link`, {
    method: "POST",
    headers: launchpadHeaders(authorization),
  }));
  const value = await response.json() as { shareUrl: string };
  return value.shareUrl;
}

export async function requestMarketplaceSwapAcceptance(
  authorization: string,
  offerId: string,
  token: string,
  selectedInscriptionIds: string[],
  makerPaymentUtxos: MempoolUtxo[],
  takerPaymentUtxos: MempoolUtxo[],
  feeRate: number,
): Promise<MarketplaceSwapAcceptanceQuote> {
  const response = await expectResponse(await fetch(`/api/marketplace/swaps/${encodeURIComponent(offerId)}/accept`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ token, selectedInscriptionIds, makerPaymentUtxos, takerPaymentUtxos, feeRate }),
  }));
  return response.json() as Promise<MarketplaceSwapAcceptanceQuote>;
}

export async function submitMarketplaceSwapTakerSignature(authorization: string, acceptanceId: string, signedPsbtBase64: string): Promise<void> {
  await expectResponse(await fetch(`/api/marketplace/swaps/acceptances/${encodeURIComponent(acceptanceId)}/taker-sign`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ signedPsbtBase64 }),
  }));
}

export async function fetchMarketplaceSwapMakerAction(authorization: string, offerId: string, token = ""): Promise<MarketplaceSwapMakerAction> {
  const params = token ? `?token=${encodeURIComponent(token)}` : "";
  const response = await expectResponse(await fetch(`/api/marketplace/swaps/${encodeURIComponent(offerId)}/maker-action${params}`, { headers: launchpadHeaders(authorization) }));
  return response.json() as Promise<MarketplaceSwapMakerAction>;
}

export async function completeMarketplaceSwap(
  authorization: string,
  acceptanceId: string,
  signedPsbtBase64: string,
  token = "",
): Promise<MarketplaceSwapCompletion> {
  const response = await expectResponse(await fetch(`/api/marketplace/swaps/acceptances/${encodeURIComponent(acceptanceId)}/complete`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ signedPsbtBase64, token }),
  }));
  return response.json() as Promise<MarketplaceSwapCompletion>;
}

export async function cancelMarketplaceSwap(authorization: string, offerId: string): Promise<void> {
  await expectResponse(await fetch(`/api/marketplace/swaps/${encodeURIComponent(offerId)}/cancel`, { method: "POST", headers: launchpadHeaders(authorization) }));
}

export async function fetchMarketplaceCollections(query = "", page = 0, sort = "name"): Promise<MarketplaceCollectionPage> {
  const params = new URLSearchParams({ query, page: String(page), sort });
  const response = await expectResponse(await fetch(`/api/marketplace/collections?${params.toString()}`, { headers: launchpadHeaders() }));
  return response.json() as Promise<MarketplaceCollectionPage>;
}

export async function fetchMarketplaceCollection(slug: string, page = 0): Promise<MarketplaceCollectionDetailPage> {
  const response = await expectResponse(await fetch(`/api/marketplace/collection/${encodeURIComponent(slug)}?page=${page}`, { headers: launchpadHeaders() }));
  return response.json() as Promise<MarketplaceCollectionDetailPage>;
}

export async function fetchMarketplaceCollectionManager(authorization: string, slug: string): Promise<MarketplaceCollectionManager> {
  const response = await expectResponse(await fetch(`/api/marketplace/collection/${encodeURIComponent(slug)}/manage`, { headers: launchpadHeaders(authorization) }));
  const result = await response.json() as { manager: MarketplaceCollectionManager };
  return result.manager;
}

export async function claimMarketplaceCollection(authorization: string, slug: string): Promise<MarketplaceCollectionManager> {
  const response = await expectResponse(await fetch(`/api/marketplace/collection/${encodeURIComponent(slug)}/claim`, { method: "POST", headers: launchpadHeaders(authorization) }));
  const result = await response.json() as { manager: MarketplaceCollectionManager };
  return result.manager;
}

export async function updateMarketplaceCollectionTheme(authorization: string, slug: string, theme: MarketplaceCollectionTheme): Promise<MarketplaceCollectionTheme> {
  const response = await expectResponse(await fetch(`/api/marketplace/collection/${encodeURIComponent(slug)}/style`, {
    method: "PUT",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(theme),
  }));
  const result = await response.json() as { theme: MarketplaceCollectionTheme };
  return result.theme;
}

export async function fetchMarketplaceInscription(inscriptionId: string): Promise<MarketplaceInscription> {
  const response = await expectResponse(await fetch(`/api/marketplace/inscription/${encodeURIComponent(inscriptionId)}`, { headers: launchpadHeaders() }));
  return response.json() as Promise<MarketplaceInscription>;
}

export async function fetchMarketplaceInscriptionByNumber(inscriptionNumber: number): Promise<MarketplaceInscription> {
  const response = await expectResponse(await fetch(
    `/api/marketplace/inscription-number?number=${encodeURIComponent(String(inscriptionNumber))}`,
    { headers: launchpadHeaders() },
  ));
  return response.json() as Promise<MarketplaceInscription>;
}

export async function fetchMarketplaceInscriptionActivity(inscriptionId: string, page = 0): Promise<MarketplaceActivityPage> {
  const response = await expectResponse(await fetch(`/api/marketplace/inscription/${encodeURIComponent(inscriptionId)}/activity?page=${page}`, { headers: launchpadHeaders() }));
  return response.json() as Promise<MarketplaceActivityPage>;
}

export async function fetchMarketplacePublicWallet(address: string, page = 0): Promise<MarketplacePublicWalletPage> {
  const response = await expectResponse(await fetch(`/api/marketplace/wallet/${encodeURIComponent(address)}?page=${page}`, { headers: launchpadHeaders() }));
  return response.json() as Promise<MarketplacePublicWalletPage>;
}

export async function fetchMarketplacePublicWalletActivity(address: string, page = 0): Promise<MarketplaceActivityPage> {
  const response = await expectResponse(await fetch(`/api/marketplace/wallet/${encodeURIComponent(address)}/activity?page=${page}`, { headers: launchpadHeaders() }));
  return response.json() as Promise<MarketplaceActivityPage>;
}

export async function fetchMarketplaceListings(collection = "", query = "", sort = "price"): Promise<MarketplaceListing[]> {
  const params = new URLSearchParams({ collection, query, sort });
  const response = await expectResponse(await fetch(`/api/marketplace/listings?${params.toString()}`, { headers: launchpadHeaders() }));
  const result = await response.json() as { listings: MarketplaceListing[] };
  return result.listings;
}

export async function fetchMarketplaceSales(collection = "", page = 0): Promise<MarketplaceSalePage> {
  const params = new URLSearchParams({ collection, page: String(page) });
  const response = await expectResponse(await fetch(`/api/marketplace/sales?${params.toString()}`, { headers: launchpadHeaders() }));
  return response.json() as Promise<MarketplaceSalePage>;
}

export async function fetchMarketplaceWallet(authorization: string, tab: MarketplaceWalletTab = "owned", page = 0, collection = "", filters: { query?: string; sort?: string; availability?: string } = {}): Promise<MarketplaceWalletPage> {
  const params = new URLSearchParams({ tab, page: String(page) });
  if (collection) params.set("collection", collection);
  if (filters.query) params.set("query", filters.query);
  if (filters.sort) params.set("sort", filters.sort);
  if (filters.availability) params.set("availability", filters.availability);
  const response = await expectResponse(await fetch(`/api/marketplace/wallet?${params.toString()}`, { headers: launchpadHeaders(authorization) }));
  return response.json() as Promise<MarketplaceWalletPage>;
}

export async function fetchMarketplaceCollectionSubmissions(authorization: string): Promise<MarketplaceCollectionSubmissionPage> {
  const response = await expectResponse(await fetch("/api/marketplace/collection-submissions", { headers: launchpadHeaders(authorization) }));
  return response.json() as Promise<MarketplaceCollectionSubmissionPage>;
}

export async function submitMarketplaceCollection(authorization: string, input: { name: string; slug: string; type: MarketplaceCollectionSubmissionType; sourceIds: string[]; xUsername: string }): Promise<MarketplaceCollectionSubmission> {
  const response = await expectResponse(await fetch("/api/marketplace/collection-submissions", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(input),
  }));
  const result = await response.json() as { submission: MarketplaceCollectionSubmission };
  return result.submission;
}

export async function reviewMarketplaceCollectionSubmission(authorization: string, id: string, approve: boolean, reviewNote = ""): Promise<MarketplaceCollectionSubmission> {
  const response = await expectResponse(await fetch(`/api/marketplace/collection-submissions/${encodeURIComponent(id)}/review`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ approve, reviewNote }),
  }));
  const result = await response.json() as { submission: MarketplaceCollectionSubmission };
  return result.submission;
}

export async function prepareMarketplaceListing(authorization: string, inscriptionId: string, priceSats: number, scope?: "bitmap"): Promise<MarketplaceListingPreparation> {
  const response = await expectResponse(await fetch(`${scope === "bitmap" ? "/api/bitmaps" : "/api/marketplace"}/listing/prepare`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ inscriptionId, priceSats }),
  }));
  return response.json() as Promise<MarketplaceListingPreparation>;
}

export async function activateMarketplaceListing(authorization: string, listingId: string, signedPsbts: Record<string, string>, scope?: "bitmap"): Promise<MarketplaceListing> {
  const response = await expectResponse(await fetch(`${scope === "bitmap" ? "/api/bitmaps" : "/api/marketplace"}/listing/${encodeURIComponent(listingId)}/submit`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ signedPsbts }),
  }));
  const result = await response.json() as { listing: MarketplaceListing };
  return result.listing;
}

export async function cancelMarketplaceListing(authorization: string, listingId: string, scope?: "bitmap"): Promise<void> {
  await expectResponse(await fetch(`${scope === "bitmap" ? "/api/bitmaps" : "/api/marketplace"}/listing/${encodeURIComponent(listingId)}/cancel`, { method: "POST", headers: launchpadHeaders(authorization) }));
}

export async function requestMarketplacePurchaseQuote(authorization: string, listingIds: string[], feeRate: number, utxos: MempoolUtxo[], scope?: "bitmap"): Promise<MarketplacePurchaseQuote> {
  const response = await expectResponse(await fetch(`${scope === "bitmap" ? "/api/bitmaps" : "/api/marketplace"}/purchase/quote`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ listingIds, feeRate, utxos }),
  }));
  return response.json() as Promise<MarketplacePurchaseQuote>;
}

export async function submitMarketplacePurchase(authorization: string, reservationId: string, signedPsbtBase64: string, scope?: "bitmap"): Promise<MarketplacePurchaseResult> {
  const response = await expectResponse(await fetch(`${scope === "bitmap" ? "/api/bitmaps" : "/api/marketplace"}/purchase/submit`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ reservationId, signedPsbtBase64 }),
  }));
  return response.json() as Promise<MarketplacePurchaseResult>;
}

export async function fetchBitmapMarket(params: URLSearchParams): Promise<BitmapMarketPage> {
  return (await expectResponse(await fetch(`/api/bitmaps/market?${params}`))).json() as Promise<BitmapMarketPage>;
}
export async function fetchOwnedBitmaps(authorization: string, page: number, snapshot = ""): Promise<BitmapOwnedPage> {
  const params = new URLSearchParams({ page: String(page), snapshot });
  return (await expectResponse(await fetch(`/api/bitmaps/owned?${params}`, { headers: launchpadHeaders(authorization) }))).json() as Promise<BitmapOwnedPage>;
}
export async function fetchBitmapDistrict(height: number): Promise<BitmapDistrict> {
  return (await expectResponse(await fetch(`/api/bitmaps/${height}`))).json() as Promise<BitmapDistrict>;
}
export async function resolveBitmap(id: string): Promise<number> {
  const data = await (await expectResponse(await fetch(`/api/bitmaps/resolve/${encodeURIComponent(id)}`))).json() as { height: number };
  return data.height;
}
export async function fetchBitmapGallery(height: number, page: number, query = ""): Promise<BitmapGalleryPage> {
  return (await expectResponse(await fetch(`/api/bitmaps/${height}/gallery?${new URLSearchParams({ page: String(page), q: query })}`))).json() as Promise<BitmapGalleryPage>;
}

export async function fetchBitmapRenderer(): Promise<BitmapRendererExport> {
  return (await expectResponse(await fetch("/api/bitmaps/renderer"))).json() as Promise<BitmapRendererExport>;
}

export async function verifyBitmapRenderer(id: string): Promise<{ inscriptionId: string; contentSha256: string }> {
  return (await expectResponse(await fetch(`/api/bitmaps/renderer/${encodeURIComponent(id)}`))).json() as Promise<{ inscriptionId: string; contentSha256: string }>;
}

export async function fetchBitmapInscription(height: number, bitmapId: string, title: string): Promise<BitmapInscriptionExport> {
  const params = new URLSearchParams({ bitmapId, title, rendererVersion: "3" });
  return (await expectResponse(await fetch(`/api/bitmaps/${height}/inscription?${params}`))).json() as Promise<BitmapInscriptionExport>;
}

export async function fetchMarketplaceRareInventory(address: string, query = "", type = "", page = 0): Promise<MarketplaceRareInventoryPage> {
  const params = new URLSearchParams({ address, query, type, page: String(page) });
  const response = await expectResponse(await fetch(`/api/marketplace/rare-inventory?${params.toString()}`, { headers: launchpadHeaders() }));
  return response.json() as Promise<MarketplaceRareInventoryPage>;
}

export async function fetchMarketplaceRareListings(query = "", type = "", sort = "recent", page = 0, seller = ""): Promise<MarketplaceRareListingPage> {
  const params = new URLSearchParams({ query, type, sort, page: String(page) });
  if (seller) params.set("seller", seller);
  const response = await expectResponse(await fetch(`/api/marketplace/rare-listings?${params.toString()}`, { headers: launchpadHeaders() }));
  return response.json() as Promise<MarketplaceRareListingPage>;
}

export async function fetchMarketplaceRareListing(listingId: string): Promise<MarketplaceRareListing> {
  const response = await expectResponse(await fetch(`/api/marketplace/rare-listing/${encodeURIComponent(listingId)}`, { headers: launchpadHeaders() }));
  const result = await response.json() as { listing: MarketplaceRareListing };
  return result.listing;
}

export async function prepareMarketplaceRareListing(
  authorization: string,
  satpoint: string,
  priceSats: number,
  feeRate: number,
  utxos: MempoolUtxo[],
): Promise<MarketplaceRareListingPreparation> {
  const response = await expectResponse(await fetch("/api/marketplace/rare-listing/prepare", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ satpoint, priceSats, feeRate, utxos }),
  }));
  return response.json() as Promise<MarketplaceRareListingPreparation>;
}

export async function activateMarketplaceRareListing(authorization: string, listingId: string, signedPsbts: Record<string, string>): Promise<MarketplaceRareListing> {
  const response = await expectResponse(await fetch(`/api/marketplace/rare-listing/${encodeURIComponent(listingId)}/submit`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ signedPsbts }),
  }));
  const result = await response.json() as { listing: MarketplaceRareListing };
  return result.listing;
}

export async function cancelMarketplaceRareListing(authorization: string, listingId: string): Promise<{ transactionId: string | null }> {
  const response = await expectResponse(await fetch(`/api/marketplace/rare-listing/${encodeURIComponent(listingId)}/cancel`, {
    method: "POST",
    headers: launchpadHeaders(authorization),
  }));
  return response.json() as Promise<{ transactionId: string | null }>;
}

export async function requestMarketplaceRarePurchaseQuote(authorization: string, listingId: string, feeRate: number, utxos: MempoolUtxo[]): Promise<MarketplaceRarePurchaseQuote> {
  const response = await expectResponse(await fetch("/api/marketplace/rare-purchase/quote", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ listingId, feeRate, utxos }),
  }));
  return response.json() as Promise<MarketplaceRarePurchaseQuote>;
}

export async function submitMarketplaceRarePurchase(authorization: string, reservationId: string, signedPsbtBase64: string): Promise<MarketplaceRarePurchaseResult> {
  const response = await expectResponse(await fetch("/api/marketplace/rare-purchase/submit", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ reservationId, signedPsbtBase64 }),
  }));
  return response.json() as Promise<MarketplaceRarePurchaseResult>;
}

export async function fetchMarketplaceRareSales(page = 0, address = ""): Promise<MarketplaceRareSalePage> {
  const params = new URLSearchParams({ page: String(page) });
  if (address) params.set("address", address);
  const response = await expectResponse(await fetch(`/api/marketplace/rare-sales?${params.toString()}`, { headers: launchpadHeaders() }));
  return response.json() as Promise<MarketplaceRareSalePage>;
}

export async function fetchMarketplaceRareWallet(authorization: string, tab: MarketplaceRareWalletTab = "inventory", page = 0): Promise<MarketplaceRareWalletPage> {
  const params = new URLSearchParams({ tab, page: String(page) });
  const response = await expectResponse(await fetch(`/api/marketplace/rare-wallet?${params.toString()}`, { headers: launchpadHeaders(authorization) }));
  return response.json() as Promise<MarketplaceRareWalletPage>;
}

export async function fetchMyCreatorProfile(authorization: string): Promise<CreatorProfile | null> {
  const response = await expectResponse(await fetch("/api/launchpad/profile/me", { headers: launchpadHeaders(authorization) }));
  const result = await response.json() as { profile: CreatorProfile | null };
  return result.profile;
}

export async function updateCreatorProfile(authorization: string, input: CreatorProfileInput): Promise<CreatorProfile> {
  const response = await expectResponse(await fetch("/api/launchpad/profile", {
    method: "PUT",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(input),
  }));
  const result = await response.json() as { profile: CreatorProfile };
  return result.profile;
}

export async function startCreatorXLink(authorization: string): Promise<string> {
  const response = await expectResponse(await fetch("/api/launchpad/profile/x/start", {
    method: "POST",
    headers: launchpadHeaders(authorization),
  }));
  const result = await response.json() as { url: string };
  return result.url;
}

export async function unlinkCreatorX(authorization: string): Promise<CreatorProfile> {
  const response = await expectResponse(await fetch("/api/launchpad/profile/x/unlink", {
    method: "POST",
    headers: launchpadHeaders(authorization),
  }));
  const result = await response.json() as { profile: CreatorProfile };
  return result.profile;
}

export async function fetchCreatorProfile(username: string): Promise<CreatorProfilePage> {
  const response = await expectResponse(await fetch(`/api/launchpad/profile/${encodeURIComponent(username)}`, { headers: launchpadHeaders() }));
  return response.json() as Promise<CreatorProfilePage>;
}

export async function fetchCreatorInscriptions(username: string, page = 0): Promise<CreatorInscriptionPage> {
  const response = await expectResponse(await fetch(`/api/launchpad/profile/${encodeURIComponent(username)}/inscriptions?page=${page}`, { headers: launchpadHeaders() }));
  return response.json() as Promise<CreatorInscriptionPage>;
}

export async function fetchCreatorOwnedInscriptions(authorization: string, page = 0, query = "", collection = ""): Promise<CreatorOwnedInscriptionPage> {
  const params = new URLSearchParams({ page: String(page) });
  if (query) params.set("query", query);
  if (collection) params.set("collection", collection);
  const response = await expectResponse(await fetch(`/api/launchpad/profile/inventory?${params.toString()}`, { headers: launchpadHeaders(authorization) }));
  return response.json() as Promise<CreatorOwnedInscriptionPage>;
}

export async function updateCreatorGallery(authorization: string, inscriptionIds: string[]): Promise<string[]> {
  const response = await expectResponse(await fetch("/api/launchpad/profile/gallery", {
    method: "PUT",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ inscriptionIds }),
  }));
  const result = await response.json() as { gallery: string[] };
  return result.gallery;
}

export async function fetchLaunches(): Promise<LaunchRecord[]> {
  const response = await expectResponse(await fetch("/api/launchpad/launches", { headers: launchpadHeaders() }));
  const result = (await response.json()) as { launches: LaunchRecord[] };
  return result.launches;
}

export async function fetchMyLaunches(authorization: string): Promise<LaunchRecord[]> {
  const response = await expectResponse(await fetch("/api/launchpad/mine", { headers: launchpadHeaders(authorization) }));
  const result = (await response.json()) as { launches: LaunchRecord[] };
  return result.launches;
}

export async function fetchLaunch(idOrSlug: string, authorization?: string): Promise<LaunchRecord> {
  const response = await expectResponse(await fetch(`/api/launchpad/launch/${encodeURIComponent(idOrSlug)}`, {
    headers: launchpadHeaders(authorization),
  }));
  return response.json() as Promise<LaunchRecord>;
}

export async function fetchLaunchpadEligibility(authorization: string, launchId: string): Promise<LaunchpadEligibility> {
  const response = await expectResponse(await fetch(`/api/launchpad/launch/${encodeURIComponent(launchId)}/eligibility`, {
    headers: launchpadHeaders(authorization),
  }));
  return response.json() as Promise<LaunchpadEligibility>;
}

export async function saveLaunch(authorization: string, draft: LaunchDraftInput): Promise<LaunchRecord> {
  const response = await expectResponse(await fetch("/api/launchpad/launch", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(draft),
  }));
  return response.json() as Promise<LaunchRecord>;
}

export async function updateLaunchPresentation(authorization: string, launchId: string, presentation: LaunchPresentationInput): Promise<LaunchRecord> {
  const response = await expectResponse(await fetch(`/api/launchpad/launch/${encodeURIComponent(launchId)}/presentation`, {
    method: "PATCH",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(presentation),
  }));
  return response.json() as Promise<LaunchRecord>;
}

export async function updateLaunchStageTimes(authorization: string, launchId: string, input: LaunchStageTimingUpdateInput): Promise<LaunchRecord> {
  const response = await expectResponse(await fetch(`/api/launchpad/launch/${encodeURIComponent(launchId)}/stages`, {
    method: "PATCH",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(input),
  }));
  return response.json() as Promise<LaunchRecord>;
}

export async function addLaunchAllowlistAllocations(
  authorization: string,
  launchId: string,
  additions: LaunchAllowlistAdditionsInput,
): Promise<LaunchRecord> {
  const response = await expectResponse(await fetch(`/api/launchpad/launch/${encodeURIComponent(launchId)}/allowlist`, {
    method: "PATCH",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(additions),
  }));
  return response.json() as Promise<LaunchRecord>;
}

export async function uploadLaunchMedia(authorization: string, kind: LaunchMediaKind, file: File): Promise<LaunchMediaUpload> {
  const response = await expectResponse(await fetch(`/api/launchpad/media/${kind}`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": file.type },
    body: file,
  }));
  return response.json() as Promise<LaunchMediaUpload>;
}

export async function fetchLaunchAssets(authorization: string, launchId: string): Promise<LaunchOnDemandAsset[]> {
  const response = await expectResponse(await fetch(`/api/launchpad/launch/${encodeURIComponent(launchId)}/assets`, {
    headers: launchpadHeaders(authorization),
  }));
  const result = await response.json() as { assets: LaunchOnDemandAsset[] };
  return result.assets;
}

export async function fetchShieldedV2Status(): Promise<ShieldedV2PublicPage> {
  const response = await expectResponse(await fetch("/api/shielded-v2/status", {
    cache: "no-store",
    headers: { accept: "application/json" },
  }));
  return response.json() as Promise<ShieldedV2PublicPage>;
}

export async function fetchShieldedHauntlingsGallery(page = 0, filter: ShieldedHauntlingsGalleryFilter = "all", revision = 0): Promise<ShieldedHauntlingsGalleryPage> {
  const response = await expectResponse(await fetch(`/api/shielded-v2/gallery?page=${page}&status=${filter}&revision=${revision}`, {
    cache: "no-store",
    headers: { accept: "application/json" },
  }));
  return response.json() as Promise<ShieldedHauntlingsGalleryPage>;
}

export async function fetchShieldedHolding(
  authorization: string,
  proof: { statement: unknown; signature: string },
): Promise<import("./shielded-ownership").ShieldedPrivateHolding> {
  const response = await expectResponse(await fetch("/api/shielded-v2/ownership/holdings", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ proofs: [proof] }),
  }));
  const result = await response.json() as { holdings: import("./shielded-ownership").ShieldedPrivateHolding[] };
  const holding = result.holdings[0];
  if (!holding) throw new Error("This private controller does not own a current Hauntling");
  return holding;
}

export async function submitShieldedOwnershipAction(
  authorization: string,
  input: {
    statement: unknown;
    signature: string;
    nextControllerPublicKey: string;
    nextCommitmentSalt: string;
  },
): Promise<{
  holding: import("./shielded-ownership").ShieldedPrivateHolding;
  receipt: import("./shielded-ownership").ShieldedOwnershipReceipt;
}> {
  const response = await expectResponse(await fetch("/api/shielded-v2/ownership/actions", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(input),
  }));
  return response.json() as Promise<{
    holding: import("./shielded-ownership").ShieldedPrivateHolding;
    receipt: import("./shielded-ownership").ShieldedOwnershipReceipt;
  }>;
}

export interface ShieldedTransferFeeQuote {
  requestId: string;
  serviceFeeSats: number;
  networkFeeSats: number;
  totalSats: number;
  psbtBase64: string;
  paymentInputIndexes: number[];
  expiresAt: number;
}

export async function quoteShieldedOwnershipTransfer(
  authorization: string,
  input: {
    statement: unknown;
    signature: string;
    nextControllerPublicKey: string;
    nextCommitmentSalt: string;
    feeRate: number;
    utxos: MempoolUtxo[];
  },
): Promise<ShieldedTransferFeeQuote> {
  const response = await expectResponse(await fetch("/api/shielded-v2/ownership/transfer/quote", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(input),
  }));
  return response.json() as Promise<ShieldedTransferFeeQuote>;
}

export async function submitShieldedOwnershipTransfer(
  authorization: string,
  requestId: string,
  signedPsbtBase64: string,
): Promise<{ transactionId: string; holding: import("./shielded-ownership").ShieldedPrivateHolding }> {
  const response = await expectResponse(await fetch("/api/shielded-v2/ownership/transfer/submit", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ requestId, signedPsbtBase64 }),
  }));
  return response.json() as Promise<{ transactionId: string; holding: import("./shielded-ownership").ShieldedPrivateHolding }>;
}

export interface ShieldedOrdinalAsset {
  id: string;
  inscriptionId: string;
  previewUrl?: string;
  assetName?: string;
  custodyOutpoint: string;
  custodyValueSats: number;
  state: "payment_pending" | "awaiting_inscription" | "inscription_pending" | "pending_confirmation" | "private_pending" | "held" | "exit_pending" | "transfer_pending";
  createdAt: number;
  updatedAt: number;
  collectionSlug: string;
  collectionName: string;
  collectionNumber?: number;
  pieceNumber?: number;
  accountManaged?: boolean;
  listingId?: string | null;
  listingPriceSats?: number | null;
}

export interface ShieldedOrdinalAssetsResponse {
  custodyAddress: string;
  assets: ShieldedOrdinalAsset[];
  pendingAssets: ShieldedOrdinalAsset[];
  operationFeeSats: number;
}

export async function fetchShieldedOrdinalAssets(authorization: string): Promise<ShieldedOrdinalAssetsResponse> {
  const response = await expectResponse(await fetch("/api/shielded-v2/shield/assets", {
    headers: launchpadHeaders(authorization),
    cache: "no-store",
  }));
  return response.json() as Promise<ShieldedOrdinalAssetsResponse>;
}

export async function fetchShieldedOrdinalAssetPreview(
  authorization: string,
  previewUrl: string,
): Promise<Blob> {
  const response = await expectResponse(await fetch(previewUrl, {
    headers: launchpadHeaders(authorization),
    cache: "no-store",
  }));
  return response.blob();
}

export async function createShieldedOrdinalListing(
  authorization: string,
  assetId: string,
  priceSats: number,
): Promise<{ listingId: string; priceSats: number }> {
  const response = await expectResponse(await fetch("/api/shielded-v2/shield/list", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ assetId, priceSats }),
  }));
  return response.json() as Promise<{ listingId: string; priceSats: number }>;
}

export async function cancelShieldedOrdinalListing(
  authorization: string,
  assetId: string,
): Promise<{ cancelled: true }> {
  const response = await expectResponse(await fetch("/api/shielded-v2/shield/list/cancel", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ assetId }),
  }));
  return response.json() as Promise<{ cancelled: true }>;
}

export interface ShieldedOrdinalPurchaseQuote {
  paymentId: string;
  saleId: string;
  listingKind: "ordinal";
  assetId: string;
  priceSats: number;
  sellerCreditSats: number;
  marketplaceFeeSats: number;
  fundingMode: "balance" | "wallet";
  poolBalanceSats: number;
  networkFeeSats: number;
  totalSats: number;
  psbtBase64?: string;
  paymentInputIndexes?: number[];
  expiresAt: number;
}

export interface ShieldedPoolPurchaseResult {
  paymentId: string;
  status: "awaiting_confirmation" | "completed";
  transactionId?: string;
  assetId?: string;
  pieceNumber?: number;
}

export async function quoteShieldedOrdinalPurchase(
  authorization: string,
  input: {
    saleId: string;
    feeRate: number;
    utxos: MempoolUtxo[];
    privateOutput?: {
      epochId: number;
      outputCommitment: string;
      controllerPublicKey: string;
      salt: string;
      encryptedNote: string;
      ephemeralPublicKey: string;
      encryptionNonce: string;
    };
  },
): Promise<ShieldedOrdinalPurchaseQuote> {
  const response = await expectResponse(await fetch("/api/shielded-v2/market/ordinal/purchase/quote", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(input),
  }));
  return response.json() as Promise<ShieldedOrdinalPurchaseQuote>;
}

export async function cancelShieldedOrdinalPurchase(authorization: string, paymentId: string): Promise<{ cancelled: true }> {
  const response = await expectResponse(await fetch("/api/shielded-v2/market/ordinal/purchase/cancel", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ paymentId }),
  }));
  return response.json() as Promise<{ cancelled: true }>;
}

export async function submitShieldedOrdinalPurchase(
  authorization: string,
  paymentId: string,
  signedPsbtBase64?: string,
): Promise<ShieldedPoolPurchaseResult> {
  const response = await expectResponse(await fetch("/api/shielded-v2/market/ordinal/purchase/submit", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ paymentId, signedPsbtBase64 }),
  }));
  return response.json() as Promise<ShieldedPoolPurchaseResult>;
}

export async function quoteShieldedOrdinalDeposit(
  authorization: string,
  input: {
    sourceOutpoint: string;
    feeRate: number;
    paymentUtxos: MempoolUtxo[];
    privateNote: {
      epochId: number;
      controllerPublicKey: string;
      salt: string;
      encryptedNote: string;
      ephemeralPublicKey: string;
      encryptionNonce: string;
    };
    zkIssuance: import("./shielded-zk-actions").ShieldedZkIssuanceProof;
  },
): Promise<{ assetId: string; quote: import("./tools").ToolsQuote }> {
  const response = await expectResponse(await fetch("/api/shielded-v2/shield/deposit/quote", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(input),
  }));
  return response.json() as Promise<{ assetId: string; quote: import("./tools").ToolsQuote }>;
}

export async function submitShieldedOrdinalDeposit(
  authorization: string,
  input: { assetId: string; authorization: string; signedPsbtBase64: string },
): Promise<{ asset: ShieldedOrdinalAsset; transaction: import("./tools").ToolsTransactionResult }> {
  const response = await expectResponse(await fetch("/api/shielded-v2/shield/deposit/submit", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(input),
  }));
  return response.json() as Promise<{ asset: ShieldedOrdinalAsset; transaction: import("./tools").ToolsTransactionResult }>;
}

export interface ShieldedSilentAuction {
  id: string;
  inscriptionId: string;
  state: "awaiting_deposit" | "scheduled" | "live" | "settling" | "settled" | "no_sale" | "cancelled" | "failed";
  startsAt: number;
  endsAt: number;
  bidCount: number;
  hasBid: boolean;
  isSeller: boolean;
  myBidSats?: number;
  reserveSats?: number;
  reserveMet?: boolean;
  soldPriceSats?: number;
  marketplaceFeeBps: number;
  marketplaceMinFeeSats: number;
  createdAt: number;
}

export interface ShieldedSilentBidQuote {
  bidId: string;
  auctionId: string;
  amountSats: number;
  previousAmountSats: number;
  topUpSats: number;
  fundingMode: "balance" | "wallet";
  poolBalanceSats: number;
  networkFeeSats: number;
  totalFromWalletSats: number;
  psbtBase64?: string;
  paymentInputIndexes?: number[];
  expiresAt: number;
}

export async function fetchShieldedSilentAuctions(
  authorization?: string,
  mine = false,
): Promise<{ auctions: ShieldedSilentAuction[] }> {
  const response = await expectResponse(await fetch(`/api/shielded-auctions${mine ? "?mine=true" : ""}`, {
    headers: launchpadHeaders(authorization),
    cache: "no-store",
  }));
  return response.json() as Promise<{ auctions: ShieldedSilentAuction[] }>;
}

export async function prepareShieldedSilentAuction(
  authorization: string,
  input: {
    sourceOutpoint: string;
    reserveSats: number;
    startsAt: number;
    endsAt: number;
    feeRate: number;
    paymentUtxos: MempoolUtxo[];
    fallbackIssuance: import("./shielded-zk-actions").ShieldedZkIssuanceProof;
  },
): Promise<{ auction: ShieldedSilentAuction; quote: import("./tools").ToolsQuote }> {
  const response = await expectResponse(await fetch("/api/shielded-auctions", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(input),
  }));
  return response.json() as Promise<{ auction: ShieldedSilentAuction; quote: import("./tools").ToolsQuote }>;
}

export async function submitShieldedSilentAuctionDeposit(
  authorization: string,
  auctionId: string,
  toolAuthorization: string,
  signedPsbtBase64: string,
): Promise<{ auction: ShieldedSilentAuction; transaction: import("./tools").ToolsTransactionResult }> {
  const response = await expectResponse(await fetch(`/api/shielded-auctions/${encodeURIComponent(auctionId)}/deposit/submit`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ authorization: toolAuthorization, signedPsbtBase64 }),
  }));
  return response.json() as Promise<{ auction: ShieldedSilentAuction; transaction: import("./tools").ToolsTransactionResult }>;
}

export async function cancelShieldedSilentAuctionDraft(
  authorization: string,
  auctionId: string,
): Promise<{ cancelled: true }> {
  const response = await expectResponse(await fetch(`/api/shielded-auctions/${encodeURIComponent(auctionId)}/cancel`, {
    method: "POST",
    headers: launchpadHeaders(authorization),
  }));
  return response.json() as Promise<{ cancelled: true }>;
}

export async function prepareShieldedSilentBid(
  authorization: string,
  auctionId: string,
  input: {
    amountSats: number;
    issuance: import("./shielded-zk-actions").ShieldedZkIssuanceProof;
    feeRate: number;
    utxos: MempoolUtxo[];
  },
): Promise<ShieldedSilentBidQuote> {
  const response = await expectResponse(await fetch(`/api/shielded-auctions/${encodeURIComponent(auctionId)}/bids`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(input),
  }));
  const result = await response.json() as { quote: ShieldedSilentBidQuote };
  return result.quote;
}

export async function cancelShieldedSilentBid(
  authorization: string,
  bidId: string,
): Promise<{ cancelled: true }> {
  const response = await expectResponse(await fetch(`/api/shielded-auctions/bids/${encodeURIComponent(bidId)}/cancel`, {
    method: "POST",
    headers: launchpadHeaders(authorization),
  }));
  return response.json() as Promise<{ cancelled: true }>;
}

export async function submitShieldedSilentBid(
  authorization: string,
  bidId: string,
  signedPsbtBase64?: string,
): Promise<{ bidId: string; state: "active" | "confirming"; transactionId?: string }> {
  const response = await expectResponse(await fetch(`/api/shielded-auctions/bids/${encodeURIComponent(bidId)}/submit`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ signedPsbtBase64 }),
  }));
  return response.json() as Promise<{ bidId: string; state: "active" | "confirming"; transactionId?: string }>;
}

export interface ShieldedOrdinalTransferQuote {
  requestId: string;
  assetId: string;
  inscriptionId: string;
  recipientAddress: string;
  serviceFeeSats: number;
  poolBalanceSats: number;
  expiresAt: number;
}

export async function quoteShieldedOrdinalTransfer(
  authorization: string,
  input: { assetId: string; recipientAddress: string },
): Promise<ShieldedOrdinalTransferQuote> {
  const response = await expectResponse(await fetch("/api/shielded-v2/shield/transfer/quote", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(input),
  }));
  return response.json() as Promise<ShieldedOrdinalTransferQuote>;
}

export async function cancelShieldedOrdinalTransfer(authorization: string, requestId: string): Promise<void> {
  await expectResponse(await fetch("/api/shielded-v2/shield/transfer/cancel", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ requestId }),
  }));
}

export async function submitShieldedOrdinalTransfer(
  authorization: string,
  requestId: string,
): Promise<{ requestId: string; assetId: string; inscriptionId: string; recipientAddress: string; transferId: string }> {
  const response = await expectResponse(await fetch("/api/shielded-v2/shield/transfer/submit", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ requestId }),
  }));
  return response.json() as Promise<{ requestId: string; assetId: string; inscriptionId: string; recipientAddress: string; transferId: string }>;
}

export interface ShieldedOrdinalExitQuote {
  requestId: string;
  assetId: string;
  inscriptionId: string;
  destinationAddress: string;
  serviceFeeSats: number;
  networkFeeSats: number;
  totalSats: number;
  psbtBase64: string;
  paymentInputIndexes: number[];
  expiresAt: number;
}

export async function quoteShieldedOrdinalExit(
  authorization: string,
  input: {
    assetId: string;
    feeRate: number;
    utxos: MempoolUtxo[];
    privateProof?: { statement: unknown; signature: string; clientNonce: string };
  },
): Promise<ShieldedOrdinalExitQuote> {
  const response = await expectResponse(await fetch("/api/shielded-v2/shield/withdraw/quote", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(input),
  }));
  return response.json() as Promise<ShieldedOrdinalExitQuote>;
}

export async function submitShieldedOrdinalExit(
  authorization: string,
  requestId: string,
  signedPsbtBase64: string,
): Promise<{ requestId: string; assetId: string; inscriptionId: string; transactionId: string }> {
  const response = await expectResponse(await fetch("/api/shielded-v2/shield/withdraw/submit", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ requestId, signedPsbtBase64 }),
  }));
  return response.json() as Promise<{ requestId: string; assetId: string; inscriptionId: string; transactionId: string }>;
}

export async function cancelShieldedOrdinalExit(authorization: string, requestId: string): Promise<{ cancelled: true }> {
  const response = await expectResponse(await fetch("/api/shielded-v2/shield/withdraw/cancel", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ requestId }),
  }));
  return response.json() as Promise<{ cancelled: true }>;
}

export interface ShieldedHauntlingsPurchaseQuote {
  paymentId: string;
  saleId: string;
  listingKind: "hauntling";
  pieceNumber: number;
  priceSats: number;
  sellerCreditSats: number;
  marketplaceFeeSats: number;
  fundingMode: "balance" | "wallet";
  poolBalanceSats: number;
  networkFeeSats: number;
  totalSats: number;
  psbtBase64?: string;
  paymentInputIndexes?: number[];
  expiresAt: number;
}

export async function quoteShieldedHauntlingsPurchase(
  authorization: string,
  input: {
    saleId: string;
    feeRate: number;
    utxos: MempoolUtxo[];
  },
): Promise<ShieldedHauntlingsPurchaseQuote> {
  const response = await expectResponse(await fetch("/api/shielded-v2/market/purchase/quote", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(input),
  }));
  return response.json() as Promise<ShieldedHauntlingsPurchaseQuote>;
}

export async function cancelShieldedHauntlingsPurchase(
  authorization: string,
  paymentId: string,
): Promise<{ cancelled: true }> {
  const response = await expectResponse(await fetch("/api/shielded-v2/market/purchase/cancel", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ paymentId }),
  }));
  return response.json() as Promise<{ cancelled: true }>;
}

export interface ShieldedAccountAsset {
  pieceNumber: number;
  inscriptionId: string;
  state: "held" | "listed" | "exit_pending";
  listingId: string | null;
  listingPriceSats: number | null;
  updatedAt: number;
}

export interface ShieldedAccountTransferQuote {
  requestId: string;
  pieceNumber: number;
  inscriptionId: string;
  recipientAddress: string;
  serviceFeeSats: number;
  poolBalanceSats: number;
  expiresAt: number;
}

export async function fetchShieldedAccountAssets(authorization: string): Promise<{ assets: ShieldedAccountAsset[]; operationFeeSats: number }> {
  const response = await expectResponse(await fetch("/api/shielded-v2/account/assets", {
    headers: launchpadHeaders(authorization),
  }));
  return response.json() as Promise<{ assets: ShieldedAccountAsset[]; operationFeeSats: number }>;
}

export async function createShieldedAccountListing(
  authorization: string,
  pieceNumber: number,
  priceSats: number,
): Promise<{ asset: ShieldedAccountAsset }> {
  const response = await expectResponse(await fetch("/api/shielded-v2/account/list", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ pieceNumber, priceSats }),
  }));
  return response.json() as Promise<{ asset: ShieldedAccountAsset }>;
}

export async function cancelShieldedAccountListing(
  authorization: string,
  pieceNumber: number,
): Promise<{ asset: ShieldedAccountAsset }> {
  const response = await expectResponse(await fetch("/api/shielded-v2/account/list/cancel", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ pieceNumber }),
  }));
  return response.json() as Promise<{ asset: ShieldedAccountAsset }>;
}

export async function quoteShieldedAccountTransfer(
  authorization: string,
  input: { pieceNumber: number; recipientAddress: string },
): Promise<ShieldedAccountTransferQuote> {
  const response = await expectResponse(await fetch("/api/shielded-v2/account/transfer/quote", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(input),
  }));
  return response.json() as Promise<ShieldedAccountTransferQuote>;
}

export async function cancelShieldedAccountTransfer(
  authorization: string,
  requestId: string,
): Promise<void> {
  await expectResponse(await fetch("/api/shielded-v2/account/transfer/cancel", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ requestId }),
  }));
}

export async function submitShieldedAccountTransfer(
  authorization: string,
  requestId: string,
): Promise<{ transferId: string; pieceNumber: number; recipientAddress: string }> {
  const response = await expectResponse(await fetch("/api/shielded-v2/account/transfer/submit", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ requestId }),
  }));
  return response.json() as Promise<{ transferId: string; pieceNumber: number; recipientAddress: string }>;
}

export async function submitShieldedHauntlingsPurchase(
  authorization: string,
  paymentId: string,
  signedPsbtBase64?: string,
): Promise<ShieldedPoolPurchaseResult> {
  const response = await expectResponse(await fetch("/api/shielded-v2/market/purchase/submit", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ paymentId, signedPsbtBase64 }),
  }));
  return response.json() as Promise<ShieldedPoolPurchaseResult>;
}

export interface ShieldedBitcoinPoolAccount {
  poolAddress: string;
  availableSats: number;
  reservedSats: number;
  recentActivity: Array<{
    id: string;
    kind: string;
    referenceId: string;
    deltaAvailableSats: number;
    deltaReservedSats: number;
    createdAt: number;
  }>;
  withdrawals: Array<{
    id: string;
    grossSats: number;
    networkFeeSats: number;
    payoutSats: number;
    destinationAddress: string;
    transactionId: string | null;
    state: string;
    createdAt: number;
  }>;
}

export interface ShieldedBitcoinPoolWithdrawalQuote {
  requestId: string;
  poolAddress: string;
  destinationAddress: string;
  grossSats: number;
  networkFeeSats: number;
  payoutSats: number;
  expiresAt: number;
}

export async function fetchShieldedBitcoinPoolAccount(authorization: string): Promise<ShieldedBitcoinPoolAccount> {
  const response = await expectResponse(await fetch("/api/shielded-v2/pool/account", {
    headers: launchpadHeaders(authorization),
  }));
  return response.json() as Promise<ShieldedBitcoinPoolAccount>;
}

export async function quoteShieldedBitcoinPoolWithdrawal(
  authorization: string,
  amountSats: number,
): Promise<ShieldedBitcoinPoolWithdrawalQuote> {
  const response = await expectResponse(await fetch("/api/shielded-v2/pool/withdraw/quote", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ amountSats }),
  }));
  return response.json() as Promise<ShieldedBitcoinPoolWithdrawalQuote>;
}

export async function cancelShieldedBitcoinPoolWithdrawal(
  authorization: string,
  requestId: string,
): Promise<{ cancelled: true }> {
  const response = await expectResponse(await fetch("/api/shielded-v2/pool/withdraw/cancel", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ requestId }),
  }));
  return response.json() as Promise<{ cancelled: true }>;
}

export async function submitShieldedBitcoinPoolWithdrawal(
  authorization: string,
  requestId: string,
): Promise<{ requestId: string; transactionId: string; payoutSats: number }> {
  const response = await expectResponse(await fetch("/api/shielded-v2/pool/withdraw/submit", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ requestId }),
  }));
  return response.json() as Promise<{ requestId: string; transactionId: string; payoutSats: number }>;
}

export interface ShieldedHauntlingsExitQuote {
  requestId: string;
  pieceNumber: number;
  destinationAddress: string;
  serviceFeeSats: number;
  networkFeeSats: number;
  totalSats: number;
  psbtBase64: string;
  paymentInputIndexes: number[];
  expiresAt: number;
}

export async function quoteShieldedHauntlingsExit(
  authorization: string,
  input: { statement: unknown; signature: string; feeRate: number; utxos: MempoolUtxo[] },
): Promise<ShieldedHauntlingsExitQuote> {
  const response = await expectResponse(await fetch("/api/shielded-v2/exit/quote", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(input),
  }));
  return response.json() as Promise<ShieldedHauntlingsExitQuote>;
}

export async function submitShieldedHauntlingsExit(
  authorization: string,
  requestId: string,
  signedPsbtBase64: string,
): Promise<{ requestId: string; pieceNumber: number; status: "awaiting-two-custody-signatures"; approvalDigest: string }> {
  const response = await expectResponse(await fetch("/api/shielded-v2/exit/submit", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ requestId, signedPsbtBase64 }),
  }));
  return response.json() as Promise<{ requestId: string; pieceNumber: number; status: "awaiting-two-custody-signatures"; approvalDigest: string }>;
}

export async function cancelShieldedHauntlingsExit(authorization: string, requestId: string): Promise<{ cancelled: true }> {
  const response = await expectResponse(await fetch("/api/shielded-v2/exit/cancel", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ requestId }),
  }));
  return response.json() as Promise<{ cancelled: true }>;
}

export async function fetchLaunchGallery(launchId: string, page = 0, authorization?: string): Promise<LaunchOnDemandGalleryPage> {
  const response = await expectResponse(await fetch(`/api/launchpad/launch/${encodeURIComponent(launchId)}/gallery?page=${page}`, {
    headers: launchpadHeaders(authorization),
  }));
  return response.json() as Promise<LaunchOnDemandGalleryPage>;
}

export async function uploadLaunchAsset(authorization: string, launchId: string, file: File): Promise<{ asset: LaunchOnDemandAsset; supply: number }> {
  const response = await expectResponse(await fetch(`/api/launchpad/launch/${encodeURIComponent(launchId)}/assets`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": file.type, "x-file-name": encodeURIComponent(file.name) },
    body: file,
  }));
  return response.json() as Promise<{ asset: LaunchOnDemandAsset; supply: number }>;
}

export async function deleteLaunchAsset(authorization: string, launchId: string, assetId: string): Promise<{ supply: number }> {
  const response = await expectResponse(await fetch(`/api/launchpad/launch/${encodeURIComponent(launchId)}/assets/${encodeURIComponent(assetId)}`, {
    method: "DELETE",
    headers: launchpadHeaders(authorization),
  }));
  return response.json() as Promise<{ supply: number }>;
}

export async function fetchLaunchAssetContent(authorization: string, launchId: string, assetId: string): Promise<Blob> {
  const response = await expectResponse(await fetch(`/api/launchpad/launch/${encodeURIComponent(launchId)}/assets/${encodeURIComponent(assetId)}/content`, {
    headers: launchpadHeaders(authorization),
  }));
  return response.blob();
}

export async function requestOnDemandMintQuote(
  authorization: string,
  launchId: string,
  feeRate: number,
  utxos: MempoolUtxo[],
): Promise<LaunchOnDemandQuote> {
  const response = await expectResponse(await fetch("/api/launchpad/on-demand/quote", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ launchId, feeRate, utxos }),
  }));
  return response.json() as Promise<LaunchOnDemandQuote>;
}

export async function requestOnDemandMintEstimate(
  launchId: string,
  feeRate: number,
  authorization?: string,
): Promise<LaunchOnDemandEstimate> {
  const response = await expectResponse(await fetch("/api/launchpad/on-demand/estimate", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ launchId, feeRate }),
  }));
  return response.json() as Promise<LaunchOnDemandEstimate>;
}

export async function submitOnDemandMint(
  authorization: string,
  reservationId: string,
  quoteAuthorization: string,
  signedPsbtBase64: string,
  shieldedCommitment?: string,
  initialOwner?: {
    controllerPublicKey: string;
    commitmentSalt: string;
    ownerCommitment: string;
    encryptedNote: string;
    ephemeralPublicKey: string;
    encryptionNonce: string;
  },
  zkIssuance?: import("./shielded-zk-actions").ShieldedZkIssuanceProof,
): Promise<LaunchpadMintResult> {
  const response = await expectResponse(await fetch("/api/launchpad/on-demand/submit", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ reservationId, authorization: quoteAuthorization, signedPsbtBase64,
      shieldedCommitment, initialOwner, zkIssuance }),
  }));
  return response.json() as Promise<LaunchpadMintResult>;
}

export async function cancelOnDemandMint(authorization: string, reservationId: string): Promise<void> {
  await expectResponse(await fetch(`/api/launchpad/on-demand/reservation/${encodeURIComponent(reservationId)}/cancel`, {
    method: "POST",
    headers: launchpadHeaders(authorization),
  }));
}

export async function requestEditionMintQuote(authorization: string, launchId: string): Promise<LaunchEditionQuote> {
  const response = await expectResponse(await fetch("/api/launchpad/edition/quote", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ launchId }),
  }));
  return response.json() as Promise<LaunchEditionQuote>;
}

export async function cancelEditionMint(authorization: string, reservationId: string): Promise<void> {
  await expectResponse(await fetch(`/api/launchpad/edition/reservation/${encodeURIComponent(reservationId)}/cancel`, {
    method: "POST",
    headers: launchpadHeaders(authorization),
  }));
}

export async function cancelLaunchpadMint(authorization: string, reservationId: string): Promise<void> {
  await expectResponse(await fetch(`/api/launchpad/mint/reservation/${encodeURIComponent(reservationId)}/cancel`, {
    method: "POST",
    headers: launchpadHeaders(authorization),
  }));
}

export async function stopLaunchpadMinting(authorization: string, launchId: string): Promise<void> {
  await expectResponse(await fetch(`/api/launchpad/launch/${encodeURIComponent(launchId)}/stop`, {
    method: "POST",
    headers: launchpadHeaders(authorization),
  }));
}

export async function fetchLaunchSigningMessage(authorization: string, launchId: string): Promise<LaunchSigningMessage> {
  const response = await expectResponse(await fetch(`/api/launchpad/launch/${encodeURIComponent(launchId)}/signing-message`, {
    headers: launchpadHeaders(authorization),
  }));
  return response.json() as Promise<LaunchSigningMessage>;
}

export async function submitLaunchForApproval(authorization: string, launchId: string, signature: string): Promise<LaunchRecord> {
  const response = await expectResponse(await fetch(`/api/launchpad/launch/${encodeURIComponent(launchId)}/publish`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ signature }),
  }));
  return response.json() as Promise<LaunchRecord>;
}

export async function fetchLaunchpadAdminIdentity(authorization: string): Promise<LaunchpadAdminIdentity> {
  const response = await expectResponse(await fetch("/api/launchpad/admin/me", { headers: launchpadHeaders(authorization) }));
  return response.json() as Promise<LaunchpadAdminIdentity>;
}

export async function fetchLaunchpadAdminQueue(
  authorization: string,
  status: LaunchpadAdminQueueStatus,
  page = 0,
): Promise<LaunchpadAdminQueue> {
  const params = new URLSearchParams({ status, page: String(page) });
  const response = await expectResponse(await fetch(`/api/launchpad/admin/launches?${params.toString()}`, {
    headers: launchpadHeaders(authorization),
  }));
  return response.json() as Promise<LaunchpadAdminQueue>;
}

export async function fetchDeferredInscriptionOrders(
  authorization: string,
): Promise<DeferredInscriptionOrder[]> {
  const response = await expectResponse(await fetch("/api/launchpad/admin/shielded-inscriptions", {
    headers: launchpadHeaders(authorization),
  }));
  const body = await response.json() as { orders: DeferredInscriptionOrder[] };
  return body.orders;
}

export async function runAutomaticDeferredInscriptionQueue(
  authorization: string,
): Promise<{
  status: "disabled" | "idle" | "completed";
  batchId?: string;
  commitTransactionId?: string;
  revealTransactionId?: string;
  inscriptionIds?: string[];
}> {
  const response = await expectResponse(await fetch("/api/launchpad/admin/shielded-inscriptions/run", {
    method: "POST",
    headers: launchpadHeaders(authorization),
  }));
  return response.json() as Promise<{
    status: "disabled" | "idle" | "completed";
    batchId?: string;
    commitTransactionId?: string;
    revealTransactionId?: string;
    inscriptionIds?: string[];
  }>;
}

export async function quoteDeferredInscriptionBatch(
  authorization: string,
  input: {
    reservationIds: string[];
    utxos: MempoolUtxo[];
    feeRate: number;
  },
): Promise<DeferredInscriptionBatchQuote> {
  const response = await expectResponse(await fetch("/api/launchpad/admin/shielded-inscriptions/quote", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(input),
  }));
  return response.json() as Promise<DeferredInscriptionBatchQuote>;
}

export async function submitDeferredInscriptionBatch(
  authorization: string,
  batchId: string,
  signedPsbtBase64: string,
): Promise<{
  batchId: string;
  commitTransactionId: string;
  revealTransactionId: string;
  inscriptionIds: string[];
}> {
  const response = await expectResponse(await fetch("/api/launchpad/admin/shielded-inscriptions/submit", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ batchId, signedPsbtBase64 }),
  }));
  return response.json() as Promise<{
    batchId: string;
    commitTransactionId: string;
    revealTransactionId: string;
    inscriptionIds: string[];
  }>;
}

export async function moderateLaunch(
  authorization: string,
  launchId: string,
  action: "approve" | "pause" | "resume" | "remove",
  note = "",
): Promise<LaunchRecord> {
  const response = await expectResponse(await fetch(`/api/launchpad/admin/launch/${encodeURIComponent(launchId)}/${action}`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ note }),
  }));
  return response.json() as Promise<LaunchRecord>;
}

export async function updateLaunchAsAdmin(
  authorization: string,
  launchId: string,
  input: LaunchAdminEditInput,
  note = "",
): Promise<LaunchRecord> {
  const response = await expectResponse(await fetch(`/api/launchpad/admin/launch/${encodeURIComponent(launchId)}`, {
    method: "PATCH",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ input, note }),
  }));
  return response.json() as Promise<LaunchRecord>;
}

export async function updateLaunchStageTimesAsAdmin(
  authorization: string,
  launchId: string,
  input: LaunchStageTimingUpdateInput,
  note = "",
): Promise<LaunchRecord> {
  const response = await expectResponse(await fetch(`/api/launchpad/admin/launch/${encodeURIComponent(launchId)}/stages`, {
    method: "PATCH",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ input, note }),
  }));
  return response.json() as Promise<LaunchRecord>;
}

export async function fetchLaunchpadPlatform(authorization?: string): Promise<LaunchpadPlatformStatus> {
  const response = await expectResponse(await fetch("/api/launchpad/platform", { headers: launchpadHeaders(authorization) }));
  return response.json() as Promise<LaunchpadPlatformStatus>;
}

export async function beginLaunchPreparation(authorization: string, launchId: string, maxPsbts: number, mintFeeOnly = false): Promise<LaunchpadPreparationBatch> {
  const response = await expectResponse(await fetch(`/api/launchpad/launch/${encodeURIComponent(launchId)}/prepare`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ maxPsbts, mintFeeOnly }),
  }));
  return response.json() as Promise<LaunchpadPreparationBatch>;
}

export async function submitLaunchPreparation(authorization: string, launchId: string, signedPsbts: Record<string, string>, mintFeeOnly = false): Promise<LaunchpadPreparationBatch> {
  const response = await expectResponse(await fetch(`/api/launchpad/launch/${encodeURIComponent(launchId)}/prepare/submit`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ signedPsbts, mintFeeOnly }),
  }));
  return response.json() as Promise<LaunchpadPreparationBatch>;
}

export async function beginShieldedLaunchPreparation(authorization: string, launchId: string, input: {
  maxItems: number;
  feeRate: number;
  paymentUtxos: MempoolUtxo[];
}): Promise<LaunchpadShieldedPreparationQuote> {
  const response = await expectResponse(await fetch(`/api/launchpad/launch/${encodeURIComponent(launchId)}/shielded-inventory/prepare`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(input),
  }));
  return response.json() as Promise<LaunchpadShieldedPreparationQuote>;
}

export async function submitShieldedLaunchPreparation(authorization: string, launchId: string, input: {
  batchId: string;
  authorization: string;
  signedPsbtBase64: string;
}): Promise<{ transactionId: string; preparedCount: number; totalCount: number; complete: boolean }> {
  const response = await expectResponse(await fetch(`/api/launchpad/launch/${encodeURIComponent(launchId)}/shielded-inventory/submit`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(input),
  }));
  return response.json() as Promise<{ transactionId: string; preparedCount: number; totalCount: number; complete: boolean }>;
}

export async function requestLaunchpadMintQuote(authorization: string, input: {
  launchId: string;
  quantity: number;
  feeRate: number;
  destinations: string[];
  utxos: MempoolUtxo[];
}): Promise<LaunchpadMintQuote | LaunchpadShieldedMintQuote> {
  const response = await expectResponse(await fetch("/api/launchpad/mint/quote", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(input),
  }));
  return response.json() as Promise<LaunchpadMintQuote | LaunchpadShieldedMintQuote>;
}

export async function submitLaunchpadMint(
  authorization: string,
  reservationId: string,
  signedPsbtBase64: string,
  zkIssuance?: import("./shielded-zk-actions").ShieldedZkIssuanceProof,
): Promise<LaunchpadMintResult> {
  const response = await expectResponse(await fetch("/api/launchpad/mint/submit", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ reservationId, signedPsbtBase64, zkIssuance }),
  }));
  return response.json() as Promise<LaunchpadMintResult>;
}

export async function fetchLaunchpadMints(launchId: string): Promise<LaunchpadMintRecord[]> {
  const response = await expectResponse(await fetch(`/api/launchpad/launch/${encodeURIComponent(launchId)}/mints`, { headers: launchpadHeaders() }));
  const result = await response.json() as { mints: LaunchpadMintRecord[] };
  return result.mints;
}

export async function fetchMyLaunchpadMints(authorization: string, launchId: string): Promise<LaunchpadMintRecord[]> {
  const response = await expectResponse(await fetch(`/api/launchpad/launch/${encodeURIComponent(launchId)}/my-mints`, { headers: launchpadHeaders(authorization) }));
  const result = await response.json() as { mints: LaunchpadMintRecord[] };
  return result.mints;
}

export async function splitLaunchpadAnchors(authorization: string, count: number, feeRate: number): Promise<{ transactionId: string; anchors: number }> {
  const response = await expectResponse(await fetch("/api/launchpad/admin/anchors/split", {
    method: "POST",
    headers: { ...adminHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ count, feeRate }),
  }));
  return response.json() as Promise<{ transactionId: string; anchors: number }>;
}

export async function fetchAdminStats(authorization: string, query = "", page = 0): Promise<AdminStatsPage> {
  const params = new URLSearchParams({ page: String(page) });
  if (query.trim()) params.set("query", query.trim());
  const response = await expectResponse(await fetch(`/api/admin/stats?${params.toString()}`, {
    headers: adminHeaders(authorization),
  }));
  return response.json() as Promise<AdminStatsPage>;
}

export async function fetchAdminWallet(authorization: string, address: string, page = 0): Promise<AdminWalletPage> {
  const params = new URLSearchParams({ address, page: String(page) });
  const response = await expectResponse(await fetch(`/api/admin/wallet?${params.toString()}`, {
    headers: adminHeaders(authorization),
  }));
  return response.json() as Promise<AdminWalletPage>;
}

export async function fetchFeatureTweetAdminPage(authorization: string): Promise<FeatureTweetAdminPage> {
  const response = await expectResponse(await fetch("/api/admin/feature-tweets", {
    headers: adminHeaders(authorization),
  }));
  return response.json() as Promise<FeatureTweetAdminPage>;
}

export async function fetchFeatureTweetHealth(authorization: string): Promise<FeatureTweetHealth> {
  const response = await expectResponse(await fetch("/api/admin/feature-tweets/health", {
    headers: adminHeaders(authorization),
  }));
  return response.json() as Promise<FeatureTweetHealth>;
}

export async function updateFeatureTweetAdminSettings(authorization: string, input: FeatureTweetSettingsInput): Promise<FeatureTweetAdminPage> {
  const response = await expectResponse(await fetch("/api/admin/feature-tweets/settings", {
    method: "PUT",
    headers: { ...adminHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(input),
  }));
  return response.json() as Promise<FeatureTweetAdminPage>;
}

export async function createFeatureTweetAdminItem(authorization: string, input: FeatureTweetInput): Promise<FeatureTweetRecord> {
  const response = await expectResponse(await fetch("/api/admin/feature-tweets", {
    method: "POST",
    headers: { ...adminHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(input),
  }));
  const result = await response.json() as { item: FeatureTweetRecord };
  return result.item;
}

export async function updateFeatureTweetAdminItem(authorization: string, id: string, input: Partial<FeatureTweetInput>): Promise<FeatureTweetRecord> {
  const response = await expectResponse(await fetch(`/api/admin/feature-tweets/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { ...adminHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(input),
  }));
  const result = await response.json() as { item: FeatureTweetRecord };
  return result.item;
}

export async function rescheduleFeatureTweetAdminItem(authorization: string, id: string, input: FeatureTweetRescheduleInput): Promise<FeatureTweetRecord> {
  const response = await expectResponse(await fetch(`/api/admin/feature-tweets/${encodeURIComponent(id)}/schedule`, {
    method: "PATCH",
    headers: { ...adminHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify(input),
  }));
  const result = await response.json() as { item: FeatureTweetRecord };
  return result.item;
}

export async function cancelFeatureTweetAdminItem(authorization: string, id: string): Promise<FeatureTweetRecord> {
  const response = await expectResponse(await fetch(`/api/admin/feature-tweets/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: adminHeaders(authorization),
  }));
  const result = await response.json() as { item: FeatureTweetRecord };
  return result.item;
}

export async function duplicateFeatureTweetAdminItem(authorization: string, id: string): Promise<FeatureTweetRecord> {
  const response = await expectResponse(await fetch(`/api/admin/feature-tweets/${encodeURIComponent(id)}/duplicate`, {
    method: "POST",
    headers: adminHeaders(authorization),
  }));
  const result = await response.json() as { item: FeatureTweetRecord };
  return result.item;
}

export async function regenerateFeatureTweetAdminScreenshot(authorization: string, id: string): Promise<FeatureTweetRecord> {
  const response = await expectResponse(await fetch(`/api/admin/feature-tweets/${encodeURIComponent(id)}/screenshot`, {
    method: "POST",
    headers: adminHeaders(authorization),
  }));
  const result = await response.json() as { item: FeatureTweetRecord };
  return result.item;
}

export async function replaceFeatureTweetAdminScreenshot(authorization: string, id: string, file: File): Promise<FeatureTweetRecord> {
  const response = await expectResponse(await fetch(`/api/admin/feature-tweets/${encodeURIComponent(id)}/screenshot`, {
    method: "POST",
    headers: { ...adminHeaders(authorization), "content-type": file.type || "image/png" },
    body: file,
  }));
  const result = await response.json() as { item: FeatureTweetRecord };
  return result.item;
}

export async function retryFeatureTweetAdminItem(authorization: string, id: string, target: "screenshot" | "post", confirm = false): Promise<FeatureTweetRecord> {
  const response = await expectResponse(await fetch(`/api/admin/feature-tweets/${encodeURIComponent(id)}/retry`, {
    method: "POST",
    headers: { ...adminHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ target, confirm }),
  }));
  const result = await response.json() as { item: FeatureTweetRecord };
  return result.item;
}

export async function manuallyPostFeatureTweetAdminItem(authorization: string, id: string, confirm: boolean): Promise<FeatureTweetRecord> {
  const response = await expectResponse(await fetch(`/api/admin/feature-tweets/${encodeURIComponent(id)}/manual-post`, {
    method: "POST",
    headers: { ...adminHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ confirm }),
  }));
  const result = await response.json() as { item: FeatureTweetRecord };
  return result.item;
}

export async function fetchUtxos(address: string, options: { includeUnconfirmed?: boolean } = {}): Promise<MempoolUtxo[]> {
  const response = await expectResponse(
    await fetch(`/api/utxos?address=${encodeURIComponent(address)}`, {
      headers: { accept: "application/json" },
    }),
  );
  const utxos = (await response.json()) as MempoolUtxo[];
  return options.includeUnconfirmed ? utxos : utxos.filter((utxo) => utxo.status.confirmed);
}

export async function prepareInscriptionBurn(
  authorization: string,
  inscriptionIds: string | string[],
  feeRate: number,
  paymentUtxos: MempoolUtxo[],
  burnMessage = "",
): Promise<InscriptionBurnQuote> {
  const selectedInscriptionIds = Array.isArray(inscriptionIds) ? inscriptionIds : [inscriptionIds];
  const response = await expectResponse(await fetch("/api/tools/burn/quote", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({
      inscriptionIds: selectedInscriptionIds,
      feeRate,
      burnMessage,
      paymentUtxos: paymentUtxos.slice(0, 50).map(({ txid, vout }) => ({ txid, vout })),
    }),
  }));
  const result = await response.json() as { quote: InscriptionBurnQuote };
  return result.quote;
}

export async function submitInscriptionBurn(
  sessionAuthorization: string,
  burnAuthorization: string,
  signedPsbtBase64: string,
  acknowledgeUnavailableSpecialSats = false,
): Promise<InscriptionBurnResult> {
  const response = await expectResponse(await fetch("/api/tools/burn/submit", {
    method: "POST",
    headers: { ...launchpadHeaders(sessionAuthorization), "content-type": "application/json" },
    body: JSON.stringify({ authorization: burnAuthorization, signedPsbtBase64, acknowledgeUnavailableSpecialSats }),
  }));
  const result = await response.json() as { result: InscriptionBurnResult };
  return result.result;
}

export async function fetchToolsConfig(): Promise<{ tools: ToolsToolDefinition[] }> {
  const response = await expectResponse(await fetch("/api/tools/config", { headers: { accept: "application/json" } }));
  return response.json() as Promise<{ tools: ToolsToolDefinition[] }>;
}

export async function fetchToolsInventory(authorization: string, preview = false, signal?: AbortSignal): Promise<ToolsInventory> {
  const response = await expectResponse(await fetch(`/api/tools/inventory${preview ? "?preview=1" : ""}`, {
    signal,
    headers: launchpadHeaders(authorization),
  }));
  return response.json() as Promise<ToolsInventory>;
}

export async function fetchRuneClaimsConfig(): Promise<RuneClaimsConfig> {
  const response = await expectResponse(await fetch("/api/rune-claims/config", { headers: { accept: "application/json" } }));
  return response.json() as Promise<RuneClaimsConfig>;
}

export async function fetchRuneEtchAvailability(name?: string, signal?: AbortSignal): Promise<RuneEtchAvailability> {
  const params = new URLSearchParams();
  if (name?.trim()) params.set("name", name.trim());
  const response = await expectResponse(await fetch(`/api/runes/etch/status${params.size ? `?${params.toString()}` : ""}`, {
    signal,
    headers: { accept: "application/json" },
  }));
  return response.json() as Promise<RuneEtchAvailability>;
}

export async function fetchRuneEtches(authorization: string): Promise<RuneEtchJobSummary[]> {
  const response = await expectResponse(await fetch("/api/runes/etches", {
    headers: launchpadHeaders(authorization),
  }));
  const result = await response.json() as { etches: RuneEtchJobSummary[] };
  return result.etches;
}

export async function submitRuneEtch(
  authorization: string,
  definition: RuneEtchDefinition,
  signedCommitHex: string,
  signedRevealHex: string,
): Promise<RuneEtchJobSummary> {
  const response = await expectResponse(await fetch("/api/runes/etches", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ definition, signedCommitHex, signedRevealHex }),
  }));
  const result = await response.json() as { etch: RuneEtchJobSummary };
  return result.etch;
}

export async function fetchRuneClaims(authorization: string): Promise<RuneClaimSummary[]> {
  const response = await expectResponse(await fetch("/api/rune-claims/claims", {
    headers: launchpadHeaders(authorization),
  }));
  const result = await response.json() as { claims: RuneClaimSummary[] };
  return result.claims;
}

export async function prepareRuneClaim(
  authorization: string,
  claimId: string,
  request: RuneClaimPrepareRequest,
): Promise<RuneClaimQuote> {
  const response = await expectResponse(await fetch(`/api/rune-claims/claims/${encodeURIComponent(claimId)}/prepare`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({
      feeRate: request.feeRate,
      idempotencyKey: request.idempotencyKey,
      paymentUtxos: request.paymentUtxos.slice(0, 50).map(({ txid, vout }) => ({ txid, vout })),
    }),
  }));
  const result = await response.json() as { quote: RuneClaimQuote };
  return result.quote;
}

export async function submitRuneClaim(
  authorization: string,
  claimId: string,
  signedPsbtBase64: string,
): Promise<RuneClaimSubmitResult> {
  const response = await expectResponse(await fetch(`/api/rune-claims/claims/${encodeURIComponent(claimId)}/submit`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ signedPsbtBase64 }),
  }));
  const result = await response.json() as { result: RuneClaimSubmitResult };
  return result.result;
}

export async function prepareToolsQuote(
  authorization: string,
  toolId: ToolsToolId,
  request: {
    feeRate: number;
    paymentUtxos: MempoolUtxo[];
    sourceOutpoints?: string[];
    sourceOutpoint?: string;
    destinationAddress?: string;
    destinationAddresses?: string[];
    isolateValueSats?: number;
    outputs?: ToolsSplitOutputRequest[];
    satBundle?: {
      destinationAddress: string;
      bundleValueSats: number;
      specialSatsPerBundle: number;
      specialSatLabel?: string;
    };
    runeName?: string;
  },
): Promise<ToolsQuote> {
  const response = await expectResponse(await fetch(`/api/tools/${encodeURIComponent(toolId)}/quote`, {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({
      ...request,
      paymentUtxos: request.paymentUtxos.slice(0, 50).map(({ txid, vout }) => ({ txid, vout })),
    }),
  }));
  const result = await response.json() as { quote: ToolsQuote };
  return result.quote;
}

export async function submitToolsTransaction(
  sessionAuthorization: string,
  toolId: ToolsToolId,
  toolAuthorization: string,
  signedPsbtBase64: string,
): Promise<ToolsTransactionResult> {
  const response = await expectResponse(await fetch(`/api/tools/${encodeURIComponent(toolId)}/submit`, {
    method: "POST",
    headers: { ...launchpadHeaders(sessionAuthorization), "content-type": "application/json" },
    body: JSON.stringify({ authorization: toolAuthorization, signedPsbtBase64 }),
  }));
  const result = await response.json() as { result: ToolsTransactionResult };
  return result.result;
}

export async function filterCardinalUtxos(utxos: MempoolUtxo[]): Promise<MempoolUtxo[]> {
  const key = (utxo: MempoolUtxo) => `${utxo.txid.toLowerCase()}:${utxo.vout}`;
  const unique = [...new Map(utxos.map(utxo => [key(utxo), utxo])).values()].sort((left, right) => right.value - left.value);
  const candidates = unique.slice(0, 250);
  const verified: MempoolUtxo[] = [];
  let unavailable = false;
  const deadline = Date.now() + 30_000;
  const indexError = () => new Error("Bitcoin output safety checks are temporarily unavailable. No payment was prepared; try reviewing again shortly.");
  for (let offset = 0; offset < candidates.length && verified.length < 50; offset += 50) {
    if (Date.now() >= deadline) { unavailable = true; break; }
    const batch = candidates.slice(offset, offset + 50);
    const requested = new Set(batch.map(key));
    let response: Response;
    try {
      response = await fetch("/api/cardinal-utxos", {
        method: "POST", headers: { "content-type": "application/json", accept: "application/json" },
        body: JSON.stringify({ outpoints: [...requested] }),
        signal: AbortSignal.timeout(Math.max(1, deadline - Date.now())),
      });
    } catch { throw indexError(); }
    await expectResponse(response);
    const result: unknown = await response.json();
    if (!result || typeof result !== "object" || !("cardinalOutpoints" in result)
      || !Array.isArray(result.cardinalOutpoints) || !result.cardinalOutpoints.every(outpoint => typeof outpoint === "string" && requested.has(outpoint))) throw indexError();
    const safe = new Set<string>(result.cardinalOutpoints);
    verified.push(...batch.filter(utxo => safe.has(key(utxo))));
    if ("unavailableOutpoints" in result) {
      if (!Array.isArray(result.unavailableOutpoints) || !result.unavailableOutpoints.every(outpoint => typeof outpoint === "string" && requested.has(outpoint) && !safe.has(outpoint))) throw indexError();
      const unknown = new Set<string>(result.unavailableOutpoints);
      if (!("protectedOutpoints" in result) || !Array.isArray(result.protectedOutpoints)
        || !result.protectedOutpoints.every(outpoint => typeof outpoint === "string" && requested.has(outpoint) && !safe.has(outpoint) && !unknown.has(outpoint))) throw indexError();
      const protectedInputs = new Set<string>(result.protectedOutpoints);
      if (new Set([...safe, ...unknown, ...protectedInputs]).size !== requested.size) throw indexError();
      unavailable ||= result.unavailableOutpoints.length > 0;
      // A global outage is not helped by sending more batches to the same node.
      if (result.unavailableOutpoints.length === requested.size) break;
    } else if (!safe.size) unavailable = true; // Older Workers cannot prove why a batch was excluded.
  }
  if (!verified.length && unavailable) throw indexError();
  if (!verified.length && unique.length > candidates.length) throw new Error("No ordinary BTC payment outputs were found in the first 250 checked. Use a BTC payment address funded with confirmed ordinary sats, then review again.");
  return verified.slice(0, 50);
}

export async function requestMarketplaceFeeQuote(
  paymentAddress: string,
  ordinalsAddress: string,
  count: number,
  feeRate: number,
  proof?: MarketplaceFeeProof,
): Promise<MarketplaceFeeQuoteResult> {
  const response = await expectResponse(await fetch("/api/fee-quote", {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ paymentAddress, ordinalsAddress, count, feeRate, proof }),
  }));
  return response.json() as Promise<MarketplaceFeeQuoteResult>;
}

export async function requestMarketplaceFeeChallenge(
  paymentAddress: string,
  ordinalsAddress: string,
  count: number,
): Promise<MarketplaceFeeChallenge> {
  const response = await expectResponse(await fetch("/api/fee-challenge", {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ paymentAddress, ordinalsAddress, count }),
  }));
  return response.json() as Promise<MarketplaceFeeChallenge>;
}

export async function requestPartnerInscriberFeeQuote(
  paymentAddress: string,
  ordinalsAddress: string,
  count: number,
  feeRate: number,
  partner?: { authorization: string; requestId: string; handoffDigest: string },
): Promise<PartnerInscriberFeeQuote> {
  const response = await expectResponse(await fetch("/api/partner/144pixels/fee-quote", {
    method: "POST",
    headers: {
      "content-type": "application/json",
      accept: "application/json",
      ...(partner ? { authorization: `Bearer ${partner.authorization}` } : {}),
    },
    body: JSON.stringify({ paymentAddress, ordinalsAddress, count, feeRate,
      ...(partner ? { requestId: partner.requestId, handoffDigest: partner.handoffDigest } : {}) }),
  }));
  const result = await response.json() as { quote: PartnerInscriberFeeQuote };
  return result.quote;
}

export async function requestPartnerAuthChallenge(input: {
  requestId: string;
  handoffDigest: string;
  partnerOrigin: string;
  provider: WalletProviderId;
  paymentAddress: string;
  paymentPublicKey: string;
  ordinalsAddress: string;
  ordinalsPublicKey: string;
}): Promise<PartnerAuthChallenge> {
  const response = await expectResponse(await fetch("/api/partner/144pixels/auth/challenge", {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(input),
  }));
  return response.json() as Promise<PartnerAuthChallenge>;
}

export async function exchangePartnerAuthSession(input: {
  requestId: string;
  challengeId: string;
  challengeToken: string;
  paymentSignature: string;
  ordinalsSignature?: string;
}): Promise<PartnerAuthSession> {
  const response = await expectResponse(await fetch("/api/partner/144pixels/auth/session", {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(input),
  }));
  return response.json() as Promise<PartnerAuthSession>;
}

export async function exchangePartnerFederatedSession(input: {
  assertion: string;
  requestId: string;
  handoffDigest: string;
  partnerOrigin: string;
  provider: WalletProviderId;
  paymentAddress: string;
  paymentPublicKey: string;
  ordinalsAddress: string;
  ordinalsPublicKey: string;
  recipient?: string;
}): Promise<PartnerAuthSession> {
  const response = await expectResponse(await fetch("/api/partner/144pixels/auth/federated", {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(input),
  }));
  return response.json() as Promise<PartnerAuthSession>;
}

export async function authorizeInscriptionCommit(
  txHex: string,
  feeAuthorization: string,
  revealTxHex: string,
  inscriptionManifest?: InscriptionOutputManifestItem[],
  rareSatManifest?: InscriptionRareSatManifestItem[],
  sessionAuthorization?: string,
): Promise<{ authorization: string; expiresAt: number }> {
  const response = await expectResponse(await fetch("/api/authorize-commit", {
    method: "POST",
    headers: {
      "content-type": "application/json",
      accept: "application/json",
      ...(sessionAuthorization ? { authorization: `Bearer ${sessionAuthorization}` } : {}),
    },
    body: JSON.stringify({ txHex, revealTxHex, feeAuthorization, inscriptionManifest, rareSatManifest }),
  }));
  return response.json() as Promise<{ authorization: string; expiresAt: number }>;
}

export async function broadcastTransaction(
  txHex: string,
  options: { role?: "raw" | "commit" | "reveal"; authorization?: string; revealTxHex?: string; commitTxHex?: string } = {},
): Promise<string> {
  let expectedTxid: string;
  try {
    expectedTxid = Transaction.fromRaw(hex.decode(txHex), {
      allowUnknownInputs: true, allowUnknownOutputs: true, disableScriptCheck: true,
    }).id;
  } catch { throw new Error("Invalid signed transaction. Nothing was sent."); }
  // Version the mutation itself: a newer client must never send to an older
  // Worker that cannot enforce the receipt and saved-reveal contract.
  let resultResponse: Response;
  try {
    resultResponse = await fetch("/api/broadcast/v2", {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify({ txHex, ...options }),
    });
  } catch {
    throw new ApiRequestError("The submission response was lost. Bitcoin acceptance still needs to be checked.", false, "unknown", false, true);
  }
  if (resultResponse.status === 404 || resultResponse.status === 405) {
    throw new ApiRequestError(
      "This app and server are running different versions. This request did not submit a transaction. Refresh the app, preserve the signed package, and use Transaction Recovery to retry; do not create a replacement inscription.",
      options.role === "reveal",
      "rejected",
    );
  }
  if (resultResponse.status === 503) {
    const failure: unknown = await resultResponse.clone().json().catch(() => null);
    if (failure && typeof failure === "object" && "code" in failure && failure.code === "BROADCAST_ACCEPTANCE_UNKNOWN"
      && "status" in failure && failure.status === "unknown" && "accepted" in failure && failure.accepted === false
      && "txid" in failure && failure.txid === expectedTxid && "error" in failure && typeof failure.error === "string") {
      throw new ApiRequestError(failure.error, false, "unknown", false, true);
    }
  }
  const response = await expectResponse(resultResponse);
  let result: unknown;
  try { result = await response.json(); }
  catch { throw new ApiRequestError("Broadcast acceptance could not be verified. Preserve the signed transaction and check its status before retrying.", false, "unknown"); }
  if (typeof result !== "object" || result === null
    || !("accepted" in result) || result.accepted !== true
    || !("source" in result) || (result.source !== "broadcast" && result.source !== "status")
    || !("txid" in result) || result.txid !== expectedTxid) {
    throw new ApiRequestError("Broadcast response did not confirm this exact transaction. Preserve the signed transaction and check its status before retrying.", false, "unknown");
  }
  return expectedTxid;
}

export async function waitForMempoolAcceptance(txid: string, attempts = 8): Promise<void> {
  if (!/^[0-9a-f]{64}$/i.test(txid)) throw new Error("Invalid transaction ID");
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const response = await fetch(`/api/tx/${txid}/status`, { headers: { accept: "application/json" }, cache: "no-store" });
    if (response.ok) {
      const status: unknown = await response.json();
      if (verifiedInscriptionTransactionStatus(txid, status)) return;
      throw new ApiRequestError("Transaction status could not be verified. Use Transaction Recovery to check again.", false, "unknown");
    }
    if (attempt + 1 < attempts) await new Promise<void>((resolve) => globalThis.setTimeout(resolve, 650 + attempt * 250));
  }
  throw new ApiRequestError("Payment acceptance is not visible yet. Use Transaction Recovery to check again before sending the inscription.", true, "unknown");
}

export async function fetchOrdinalOutputs(
  address: string,
  type: "any" | "cardinal" | "inscribed" = "cardinal",
): Promise<OrdinalOutputRecord[]> {
  const response = await expectResponse(await fetch(
    `/api/ordinals/outputs?address=${encodeURIComponent(address)}&type=${type}`,
    { headers: { accept: "application/json" } },
  ));
  return response.json() as Promise<OrdinalOutputRecord[]>;
}

export async function fetchOrdinalInscription(id: string): Promise<OrdinalInscriptionRecord> {
  const response = await expectResponse(await fetch(`/api/ordinals/inscription/${encodeURIComponent(id)}`, {
    headers: { accept: "application/json" },
  }));
  return response.json() as Promise<OrdinalInscriptionRecord>;
}

export interface ResolvedImageInscription {
  requestedId: string;
  contentId: string;
  contentType: string;
  contentLength: number;
  delegateDepth: number;
}

export async function resolveImageInscription(id: string, allowHtml = false): Promise<ResolvedImageInscription> {
  const requestedId = id.trim();
  let contentId = requestedId;
  const visited = new Set<string>();

  for (let delegateDepth = 0; delegateDepth <= 8; delegateDepth += 1) {
    const normalizedId = contentId.toLowerCase();
    if (visited.has(normalizedId)) throw new Error("This image inscription has a circular delegate chain");
    visited.add(normalizedId);

    const inscription = await fetchOrdinalInscription(contentId);
    const delegate = inscription.delegate?.trim();
    if (delegate) {
      contentId = delegate;
      continue;
    }

    const contentType = inscription.content_type?.trim() ?? "";
    const contentLength = Number(inscription.content_length ?? 0);
    if (allowHtml ? !isGalleryContentType(contentType) : !contentType.startsWith("image/")) throw new Error(`The selected inscription does not resolve to an image${allowHtml ? " or HTML" : ""}`);
    if (!Number.isSafeInteger(contentLength) || contentLength <= 0) throw new Error("The selected image inscription has no content");
    return { requestedId, contentId, contentType, contentLength, delegateDepth };
  }

  throw new Error("This image has too many delegate links");
}

async function fetchOrdinalContent(id: string, allowHtml: boolean): Promise<{ body: Uint8Array; mimeType: string }> {
  const response = await expectResponse(await fetch(`/api/ordinals/content/${encodeURIComponent(id)}`, {
    headers: { accept: allowHtml ? "image/*,text/html" : "image/*" },
  }));
  const contentType = response.headers.get("content-type") ?? "";
  if (allowHtml ? !isGalleryContentType(contentType) : !contentType.startsWith("image/")) throw new Error(`The selected inscription does not contain an image${allowHtml ? " or HTML" : ""}`);
  const bytes = new Uint8Array(await response.arrayBuffer());
  if (!bytes.length) throw new Error("The selected image inscription has no content");
  return { body: bytes, mimeType: contentType };
}

export async function fetchOrdinalImageContent(id: string): Promise<Uint8Array> {
  return (await fetchOrdinalContent(id, false)).body;
}

export async function fetchOrdinalGalleryContent(id: string): Promise<{ body: Uint8Array; mimeType: string }> {
  return fetchOrdinalContent(id, true);
}

export async function fetchSpecialSatProfiles(address: string): Promise<SpecialSatProfile[]> {
  const response = await expectResponse(await fetch(
    `/api/ordinals/special-sats?address=${encodeURIComponent(address)}`,
    { headers: { accept: "application/json" } },
  ));
  const result = (await response.json()) as { results?: SpecialSatProfile[] };
  return Array.isArray(result.results) ? result.results : [];
}

export async function resolveGallerySpecificSat(
  authorization: string,
  reference: string,
): Promise<SpecificSatResolution> {
  const response = await expectResponse(await fetch("/api/ordinals/specific-sat/resolve", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ reference }),
  }));
  const result = await response.json() as { resolution: SpecificSatResolution };
  return result.resolution;
}

export async function resolvePartnerSpecificSat(
  authorization: string,
  reference: string,
  requestId: string,
  handoffDigest: string,
): Promise<SpecificSatResolution> {
  const response = await expectResponse(await fetch("/api/partner/144pixels/specific-sat/resolve", {
    method: "POST",
    headers: { ...launchpadHeaders(authorization), "content-type": "application/json" },
    body: JSON.stringify({ reference, requestId, handoffDigest }),
  }));
  const result = await response.json() as { resolution: SpecificSatResolution };
  return result.resolution;
}

export async function outpointIsUnspent(outpoint: string): Promise<boolean> {
  const [txid, vout] = outpoint.split(":");
  const response = await expectResponse(await fetch(`/api/tx/${txid}/outspend/${vout}`, {
    headers: { accept: "application/json" },
  }));
  const result = (await response.json()) as { spent: boolean };
  return !result.spent;
}

export async function resolveSpecificSat(address: string, sat: number): Promise<OrdinalSource> {
  if (!Number.isSafeInteger(sat) || sat < 0) throw new Error("Enter a whole, non-negative sat number");
  const outputs = await fetchOrdinalOutputs(address, "cardinal");
  const output = outputs.find((candidate) => candidate.value <= 10_000 && candidate.sat_ranges?.[0]?.[0] === sat);
  if (!output) throw new Error("That sat is not the first sat of an eligible wallet output");
  if (!(await outpointIsUnspent(output.outpoint))) throw new Error("That sat output is already spent or in the mempool");
  return { outpoint: output.outpoint, value: output.value, address, satRanges: output.sat_ranges };
}

const rarityOrder = new Map([
  ["mythic", 0],
  ["legendary", 1],
  ["epic", 2],
  ["rare", 3],
  ["uncommon", 4],
  ["common", 5],
]);

function sortWalletSatSources(sources: WalletSatSource[]): WalletSatSource[] {
  return sources.sort((left, right) => {
    const leftSpecial = left.primarySlug === "unknown" ? 2 : left.primarySlug !== left.rarity ? 0 : 1;
    const rightSpecial = right.primarySlug === "unknown" ? 2 : right.primarySlug !== right.rarity ? 0 : 1;
    return leftSpecial - rightSpecial
      || (rarityOrder.get(left.rarity) ?? 9) - (rarityOrder.get(right.rarity) ?? 9)
      || left.sat - right.sat;
  });
}

export async function fetchWalletSatSources(address: string): Promise<WalletSatSource[]> {
  const outputs = await fetchOrdinalOutputs(address, "any");
  return sortWalletSatSources(outputs
    .map((output) => walletSatFromOutput(output, address))
    .filter((source): source is WalletSatSource => source !== null)
    .map(withUnknownSpecialSatCoverage));
}

// Standard Ord rarity is not proof that a sat has no exotic categories.
// A partial positive catalogue must never label its unobserved remainder common.
export function withUnknownSpecialSatCoverage(source: WalletSatSource): WalletSatSource {
  return {
    ...source,
    specialSatCoverage: "partial",
    primarySlug: source.primarySlug === "common" ? "unknown" : source.primarySlug,
    primaryLabel: source.primarySlug === "common" ? "Unknown" : source.primaryLabel,
    attributes: source.attributes.filter(attribute => attribute.slug !== "common"),
  };
}

export function enrichWalletSatSourcesWithProfiles(
  sources: WalletSatSource[],
  specialProfiles: SpecialSatProfile[],
): WalletSatSource[] {
  const specialByOutpoint = new Map(specialProfiles.map((profile) => [profile.outpoint, profile]));
  return sortWalletSatSources(sources.map((source) => {
    const profile = specialByOutpoint.get(source.outpoint);
    if (!profile || !source.satRanges) return withUnknownSpecialSatCoverage(source);
    return withUnknownSpecialSatCoverage(walletSatFromOutput({
      outpoint: source.outpoint,
      value: source.value,
      address: source.address,
      spent: false,
      inscriptions: [],
      runes: {},
      sat_ranges: source.satRanges,
    }, source.address, profile) ?? source);
  }));
}

export async function enrichWalletSatSources(
  address: string,
  sources: WalletSatSource[],
): Promise<WalletSatSource[]> {
  const specialProfiles = await fetchSpecialSatProfiles(address);
  return enrichWalletSatSourcesWithProfiles(sources, specialProfiles);
}

export async function resolveWalletSatSources(
  address: string,
  selections: Array<Pick<WalletSatSource, "outpoint" | "sat" | "primarySlug" | "primaryLabel" | "attributes">>,
): Promise<WalletSatSource[]> {
  if (
    selections.length > 500
    || new Set(selections.map((selection) => selection.sat)).size !== selections.length
    || new Set(selections.map((selection) => selection.outpoint)).size !== selections.length
  ) throw new Error("Selected sats must be unique");
  const outputs = await fetchOrdinalOutputs(address, "any");
  const byOutpoint = new Map(outputs.map((output) => [output.outpoint, output]));
  const selected = selections.map((selection) => {
    const output = byOutpoint.get(selection.outpoint);
    const source = output ? walletSatFromOutput(output, address, {
      outpoint: selection.outpoint,
      sat: selection.sat,
      primarySlug: selection.primarySlug,
      primaryLabel: selection.primaryLabel,
      attributes: selection.attributes,
    }) : null;
    if (!source || source.sat !== selection.sat) throw new Error(`Sat ${selection.sat} is no longer available in this wallet`);
    if (!walletSatSourceIsUsable(source)) throw new Error(`Sat ${selection.sat} is not the first sat of its UTXO`);
    return source;
  });
  const unspent = await Promise.all(selected.map((source) => outpointIsUnspent(source.outpoint)));
  if (unspent.some((value) => !value)) throw new Error("A selected sat has already been spent or is in the mempool");
  return selected;
}

export async function resolveOwnedInscription(address: string, id: string): Promise<OrdinalSource> {
  const inscription = await fetchOrdinalInscription(id);
  const [txid, vout, offset] = inscription.satpoint.split(":");
  if (inscription.address !== address) throw new Error("That inscription is not held by the connected Ordinals address");
  const outpoint = `${txid}:${vout}`;
  if (!(await outpointIsUnspent(outpoint))) throw new Error("That inscription output is already spent or in the mempool");
  return {
    outpoint,
    value: inscription.value,
    address,
    inscriptionId: inscription.id,
    satpoint: inscription.satpoint,
    satRanges: offset === "0" ? undefined : [],
  };
}

export async function resolveReinscription(address: string, id: string): Promise<OrdinalSource> {
  const source = await resolveOwnedInscription(address, id);
  if (source.satpoint?.split(":")[2] !== "0") throw new Error("Reinscription requires an inscription at offset zero");
  return source;
}

async function fetch144PixelsAsset(url: string, kind: "manifest" | "piece"): Promise<Response> {
  return expectResponse(await fetch("/api/partner/144pixels/fetch", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ url, kind }),
  }));
}

export async function fetch144PixelsManifest(url: string): Promise<unknown> {
  return (await fetch144PixelsAsset(url, "manifest")).json() as Promise<unknown>;
}

export interface PartnerPieceBytes {
  body: Uint8Array;
  contentType: string;
  sha256: string;
}

export async function fetch144PixelsPiece(url: string): Promise<PartnerPieceBytes> {
  const response = await fetch144PixelsAsset(url, "piece");
  const body = new Uint8Array(await response.arrayBuffer());
  const contentType = response.headers.get("content-type")?.split(";")[0]?.trim() || "application/octet-stream";
  const sha256 = response.headers.get("x-stc-content-sha256") ?? "";
  if (!/^[0-9a-f]{64}$/i.test(sha256) || Number(response.headers.get("x-stc-content-length")) !== body.length) {
    throw new Error("The byte-exact 144 Pixels file could not be verified");
  }
  return { body, contentType, sha256: sha256.toLowerCase() };
}

export async function fetchTemporaryRuneClaim(authorization: string): Promise<import("./rune-claims").TemporaryRuneClaimStatus> {
  const response = await expectResponse(await fetch("/api/rune-claims/temporary", { headers: launchpadHeaders(authorization) }));
  return response.json() as Promise<import("./rune-claims").TemporaryRuneClaimStatus>;
}

export async function requestTemporaryRuneClaim(authorization: string): Promise<RuneClaimSummary> {
  const response = await expectResponse(await fetch("/api/rune-claims/temporary/request", {
    method: "POST", headers: launchpadHeaders(authorization),
  }));
  const result = await response.json() as { claim: RuneClaimSummary };
  return result.claim;
}
